import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams, useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { DialogoFechaCaja } from "../components/DialogoFechaCaja";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { 
  Building2, 
  FileSpreadsheet, 
  Plus, 
  Search, 
  MoreHorizontal, 
  Edit, 
  Trash2,
  Ban,
  Printer,
  ChevronsUpDown,
  Check,
  ArrowLeft,
  PlusCircle,
  MinusCircle,
  FileCheck,
  Download,
  FileJson,
  FileType,
  File,
  Send,
  X,
  RefreshCw
} from "lucide-react";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API = process.env.REACT_APP_BACKEND_URL;

const DesarrolloUrbanoPage = () => {
  const { submodulo: submoduloParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const submodulo = submoduloParam || searchParams.get("sub") || null;

  // Data states
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  // RIF dropdown states
  const [rifPopoverOpen, setRifPopoverOpen] = useState(false);
  const [contribuyentes, setContribuyentes] = useState([]);
  
  // Contribuyentes con LAE para dropdown de Nombre Comercio
  const [contribuyentesLAE, setContribuyentesLAE] = useState([]);
  const [nombreComercioPopoverOpen, setNombreComercioPopoverOpen] = useState(false);
  const [nroLaePopoverOpen, setNroLaePopoverOpen] = useState(false);
  
  // Tipos de impuestos dropdown
  const [tiposImpuestoPopoverOpen, setTiposImpuestoPopoverOpen] = useState(false);
  const [tiposImpuesto, setTiposImpuesto] = useState([]);
  
  // Zonificación dropdown
  const [zonificacionPopoverOpen, setZonificacionPopoverOpen] = useState(false);
  
  // Cotización activa
  const [cotizacionActiva, setCotizacionActiva] = useState(null);

  // Búsqueda de tipos de impuesto
  const [tipoImpuestoBusqueda, setTipoImpuestoBusqueda] = useState("");

  // Estados para dropdown de Código Catastral (Boletín - Cálculo)
  const [codigoCatastralPopoverOpen, setCodigoCatastralPopoverOpen] = useState(false);
  const [codigosCatastralesPorRif, setCodigosCatastralesPorRif] = useState([]);
  const [codigoCatastralBusqueda, setCodigoCatastralBusqueda] = useState("");
  const [sinCodigoCatastral, setSinCodigoCatastral] = useState(false);

  // Estados para filtros individuales de Boletín - Cálculo
  const [boletinDUFilters, setBoletinDUFilters] = useState({
    nro_boletin: "",
    rif: "",
    nombre_razon_social: "",
    fecha: "",
    codigo_catastral: "",
    estatus: "",
    estatus_pago: ""
  });

  // Estados para filtros individuales de Conformidad de Uso
  const [conformidadFilters, setConformidadFilters] = useState({
    nro_conformidad: "",
    rif: "",
    rif_contribuyente: "",
    nombre_razon_social: "",
    lae: "",
    direccion: "",
    fecha_emision: "",
    fecha_vencimiento: "",
    estatus: ""
  });

  // Initial edit load flag
  const [initialEditLoad, setInitialEditLoad] = useState(false);

  // Estado para diálogo de confirmación "Enviar a Caja"
  const [enviarCajaDialogOpen, setEnviarCajaDialogOpen] = useState(false);
  const [boletinParaEnviar, setBoletinParaEnviar] = useState(null);
  const [fechaCajaDialogOpen, setFechaCajaDialogOpen] = useState(false);
  const [enviarConFechaActualizada, setEnviarConFechaActualizada] = useState(false);

  const esMesActual = (fecha) => {
    if (!fecha) return false;
    const hoy = new Date();
    const [y, m] = fecha.split("-");
    return parseInt(y) === hoy.getFullYear() && parseInt(m) === (hoy.getMonth() + 1);
  };

  // Estado para validación del recibo de pago en Conformidad de Uso
  const [reciboValidacion, setReciboValidacion] = useState({ validando: false, valido: null, mensaje: "" });

  // Estado para diálogo de error de validación de recibo
  const [reciboErrorDialogOpen, setReciboErrorDialogOpen] = useState(false);

  // Estado para diálogo de advertencia de impresión (solo solventes)
  const [printWarningDialogOpen, setPrintWarningDialogOpen] = useState(false);
  const [printWarningEstatus, setPrintWarningEstatus] = useState("");

  // Auth
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch data based on submodulo
  const fetchData = async () => {
    if (!submodulo) return;
    try {
      const endpoint = getEndpoint(submodulo);
      const response = await axios.get(`${API}/api/desarrollo-urbano/${endpoint}`, { headers });
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    }
  };

  // Fetch contribuyentes
  const fetchContribuyentes = async (clase) => {
    try {
      const claseParam = clase === "Contribuyente con LAE" ? "con-lae" : "sin-lae";
      const response = await axios.get(`${API}/api/contribuyentes/buscar?clase=${claseParam}`, { headers });
      setContribuyentes(response.data);
    } catch (error) {
      toast.error("Error al cargar contribuyentes");
    }
  };

  // Fetch contribuyentes con LAE para dropdown de Nombre Comercio
  const fetchContribuyentesLAE = async () => {
    try {
      const response = await axios.get(`${API}/api/contribuyentes/con-lae`, { headers });
      setContribuyentesLAE(response.data);
    } catch (error) {
      console.error("Error al cargar contribuyentes con LAE");
    }
  };
  
  // Fetch tipos de impuesto - Filtrado por oficina Desarrollo Urbano
  const OFICINA_DESARROLLO_URBANO_ID = "86d0139a-3352-40e5-8c4b-62f84c07180a";
  const TIPO_CALCULO_URBANO_ID = "5fd305ea-cd68-4286-9ad1-aed021df83d0";
  const TIPO_CALCULO_TASA_ID = "f7afec5f-2276-4b70-9a7b-d07c0f42bfbb";
  
  const fetchTiposImpuesto = async () => {
    try {
      const response = await axios.get(`${API}/api/definiciones/tipos-impuesto?oficina_id=${OFICINA_DESARROLLO_URBANO_ID}`, { headers });
      setTiposImpuesto(response.data);
    } catch (error) {
      console.error("Error al cargar tipos de impuesto");
    }
  };
  
  // Función para calcular Ingreso Bruto según fórmula urbana
  // Ingreso Bruto = TCMMV × Unidad de Cuenta × m² o Unidad(es)
  const calcularIngresoBrutoUrbano = (tcmmv, metrosCuadrados) => {
    const unidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
    return (tcmmv || 0) * unidadCuenta * (metrosCuadrados || 0);
  };
  
  // Fetch cotización activa
  const fetchCotizacionActiva = async () => {
    try {
      const response = await axios.get(`${API}/api/definiciones/cotizaciones`, { headers });
      const activa = response.data.find(c => c.estatus === "Activo");
      setCotizacionActiva(activa);
    } catch (error) {
      console.error("Error al cargar cotización activa");
    }
  };

  // Fetch códigos catastrales por RIF (para Boletín - Cálculo)
  const fetchCodigosCatastralesPorRif = async (rif) => {
    if (!rif) {
      setCodigosCatastralesPorRif([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/api/catastro/ficha-catastral`, { headers });
      const fichasFiltradas = response.data
        .filter(ficha => ficha.rif === rif && ficha.codigo_catastral)
        .map(ficha => ({
          id: ficha.id,
          codigo: ficha.codigo_catastral,
          direccion: ficha.direccion_ubicacion || ficha.direccion || ""
        }));
      setCodigosCatastralesPorRif(fichasFiltradas);
    } catch (error) {
      console.error("Error al cargar códigos catastrales:", error);
      setCodigosCatastralesPorRif([]);
    }
  };

  // Buscar ficha catastral por RIF y auto-llenar el código catastral (para Conformidad de Uso)
  const fetchFichaCatastralPorRif = async (rif) => {
    if (!rif) return null;
    try {
      const response = await axios.get(`${API}/api/catastro/ficha-catastral`, { headers });
      // Buscar la primera ficha que coincida con el RIF y tenga código catastral
      const fichaEncontrada = response.data.find(
        ficha => ficha.rif === rif && ficha.codigo_catastral
      );
      return fichaEncontrada ? fichaEncontrada.codigo_catastral : null;
    } catch (error) {
      console.error("Error al buscar ficha catastral:", error);
      return null;
    }
  };

  // Validar recibo de pago para Conformidad de Uso
  const validarReciboConformidad = async (nroRecibo) => {
    if (!nroRecibo || !formData.clase_contribuyente || !formData.rif) {
      setReciboValidacion({ validando: false, valido: null, mensaje: "" });
      return;
    }
    
    setReciboValidacion({ validando: true, valido: null, mensaje: "Validando..." });
    
    try {
      const params = new URLSearchParams({
        nro_recibo: nroRecibo,
        clase_contribuyente: formData.clase_contribuyente,
        rif: formData.rif
      });
      
      const response = await axios.get(`${API}/api/recaudaciones/validar-recibo-conformidad?${params}`, { headers });
      
      if (response.data.valido) {
        setReciboValidacion({ validando: false, valido: true, mensaje: response.data.mensaje });
        // Cambiar automáticamente el estatus a "Solvente" cuando el recibo es válido
        setFormData(prev => ({...prev, estatus: "Solvente"}));
        toast.success(response.data.mensaje);
      } else {
        setReciboValidacion({ validando: false, valido: false, mensaje: response.data.mensaje });
        // Mantener estatus como "Moroso" si el recibo no es válido
        setFormData(prev => ({...prev, estatus: "Moroso"}));
        toast.error(response.data.mensaje);
      }
    } catch (error) {
      console.error("Error al validar recibo:", error);
      setReciboValidacion({ validando: false, valido: false, mensaje: "Error al validar el recibo" });
      toast.error("Error al validar el recibo");
    }
  };

  const getEndpoint = (sub) => {
    switch (sub) {
      case "boletin-calculo": return "boletines-calculo";
      case "conformidad-uso": return "conformidad-uso";
      default: return "";
    }
  };

  useEffect(() => {
    if (submodulo) {
      fetchData();
      if (submodulo === "boletin-calculo") {
        fetchTiposImpuesto();
        fetchCotizacionActiva();
      }
      if (submodulo === "conformidad-uso") {
        fetchContribuyentesLAE();
      }
    }
  }, [submodulo]);

  // Effect to reload contribuyentes when clase changes
  useEffect(() => {
    if (formData.clase_contribuyente) {
      if (!initialEditLoad) {
        const updates = { rif: "", contribuyente_id: "", nombre_razon_social: "" };
        setFormData(prev => ({ ...prev, ...updates }));
      } else {
        setInitialEditLoad(false);
      }
      fetchContribuyentes(formData.clase_contribuyente);
    }
  }, [formData.clase_contribuyente]);

  // Effect to recalculate when tipo_impuesto or metros_cuadrados change
  useEffect(() => {
    if (submodulo === "boletin-calculo" && formData.tipo_impuesto_id) {
      // Buscar el tipo de impuesto seleccionado
      const tipoSeleccionado = tiposImpuesto.find(t => t.id === formData.tipo_impuesto_id);
      
      if (!tipoSeleccionado) return;
      
      const unidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
      let ingresoBruto = 0;
      let importe = 0;
      let subTotal = 0;
      let tipoCalculo = "";
      
      if (tipoSeleccionado.tipo_calculo_id === TIPO_CALCULO_URBANO_ID) {
        // Fórmula URBANO: Ingreso Bruto = TCMMV × Unidad de Cuenta × m²
        const tcmmv = tipoSeleccionado.tcmmv || 0;
        const metrosCuadrados = formData.metros_cuadrados || 0;
        
        ingresoBruto = tcmmv * unidadCuenta * metrosCuadrados;
        importe = tipoSeleccionado.tasa || 0;
        subTotal = ingresoBruto * (importe / 100);
        tipoCalculo = "urbano";
        
      } else if (tipoSeleccionado.tipo_calculo_id === TIPO_CALCULO_TASA_ID) {
        // Fórmula TASA: Ingreso Bruto = Unidad de Cuenta, Importe = Tasa
        ingresoBruto = unidadCuenta;
        importe = tipoSeleccionado.tasa || 0;
        subTotal = ingresoBruto * importe;
        tipoCalculo = "tasa";
      }
      
      if (tipoCalculo) {
        // Crear o actualizar línea de impuesto automática
        const lineaCalculada = {
          tipo_impuesto_id: tipoSeleccionado.id,
          impuesto_tasa: tipoSeleccionado.nombre,
          ingreso_bruto: ingresoBruto,
          importe: importe,
          sub_total: subTotal,
          tipo_calculo: tipoCalculo
        };
        
        // Verificar si ya existe una línea con este tipo de impuesto
        const lineasActuales = formData.lineas_impuestos || [];
        const indexExistente = lineasActuales.findIndex(l => l.tipo_impuesto_id === tipoSeleccionado.id);
        
        let nuevasLineas;
        if (indexExistente >= 0) {
          // Actualizar línea existente
          nuevasLineas = [...lineasActuales];
          nuevasLineas[indexExistente] = lineaCalculada;
        } else {
          // Agregar nueva línea
          nuevasLineas = [...lineasActuales, lineaCalculada];
        }
        
        setFormData(prev => ({
          ...prev,
          lineas_impuestos: nuevasLineas
        }));
      }
    }
  }, [formData.tipo_impuesto_id, formData.metros_cuadrados, tiposImpuesto, cotizacionActiva]);

  // Effect to calculate totals
  useEffect(() => {
    if (submodulo === "boletin-calculo" && formData.lineas_impuestos) {
      // Calcular Sub Total sumando los sub_total de las líneas
      const subTotalSum = (formData.lineas_impuestos || []).reduce(
        (acc, linea) => acc + (linea.sub_total || 0), 0
      );
      
      // Calcular Total = Sub Total - Descuento - Reintegro - Retención + Multa
      const descuento = formData.descuento || 0;
      const reintegro = formData.reintegro || 0;
      const retencion = formData.retencion || 0;
      const multa = formData.multa || 0;
      const total = subTotalSum - descuento - reintegro - retencion + multa;
      
      // Calcular Divisas
      const tasaDolar = cotizacionActiva?.dolar || 1;
      const tasaEuro = cotizacionActiva?.euro || 1;
      const divisasUsd = tasaDolar > 0 ? total / tasaDolar : 0;
      const divisasEur = tasaEuro > 0 ? total / tasaEuro : 0;
      
      const updates = {};
      if (formData.sub_total !== subTotalSum) updates.sub_total = subTotalSum;
      if (formData.total !== total) updates.total = total;
      if (formData.divisas !== divisasUsd) updates.divisas = divisasUsd;
      if (formData.divisas_euro !== divisasEur) updates.divisas_euro = divisasEur;
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [formData.lineas_impuestos, formData.descuento, formData.reintegro, formData.retencion, formData.multa, submodulo, cotizacionActiva]);

  // Effect to recalculate Urbano lines when metros_cuadrados changes
  useEffect(() => {
    if (submodulo === "boletin-calculo" && formData.lineas_impuestos && formData.lineas_impuestos.length > 0) {
      const unidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
      const metrosCuadrados = formData.metros_cuadrados || 0;
      
      let hasChanges = false;
      const updatedLineas = formData.lineas_impuestos.map(linea => {
        if (linea.es_tipo_urbano) {
          const tcmmv = linea.tcmmv || 0;
          const ingresoBruto = tcmmv * unidadCuenta * metrosCuadrados;
          const importe = linea.importe || 0;
          const subTotal = ingresoBruto * (importe / 100);
          
          if (linea.ingreso_bruto !== ingresoBruto || linea.sub_total !== subTotal) {
            hasChanges = true;
            return { ...linea, ingreso_bruto: ingresoBruto, sub_total: subTotal };
          }
        }
        return linea;
      });
      
      if (hasChanges) {
        setFormData(prev => ({ ...prev, lineas_impuestos: updatedLineas }));
      }
    }
  }, [formData.metros_cuadrados, cotizacionActiva, submodulo]);

  // CRUD operations
  const validateBoletinForm = () => {
    const requiredFields = [
      { field: 'clase_contribuyente', label: 'Clase de Contribuyente' },
      { field: 'rif', label: 'RIF / Contribuyente' },
      { field: 'fecha', label: 'Fecha' },
      { field: 'mes_anio_boletin', label: 'Mes/Año Boletín' }
    ];
    
    const missingFields = requiredFields.filter(({ field }) => !formData[field]);
    
    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(f => f.label).join(', ');
      toast.error(`Campos obligatorios: ${fieldNames}`);
      return false;
    }
    
    // Validar que haya al menos un tipo de impuesto seleccionado
    if (!formData.tipos_impuesto_seleccionados || formData.tipos_impuesto_seleccionados.length === 0) {
      toast.error('Debe seleccionar al menos un Tipo de Impuesto');
      return false;
    }
    
    // Validar que haya al menos una línea de impuesto con tipo seleccionado
    const lineasConTipo = (formData.lineas_impuestos || []).filter(l => l.tipo_impuesto_id);
    if (lineasConTipo.length === 0) {
      toast.error('Debe agregar al menos una línea de impuesto con tipo seleccionado');
      return false;
    }
    
    return true;
  };

  const handleCreate = async () => {
    try {
      const endpoint = getEndpoint(submodulo);
      
      // Validar campos obligatorios para boletín
      if (submodulo === "boletin-calculo" && !validateBoletinForm()) {
        return;
      }
      
      // Validar recibo de pago para conformidad de uso
      if (submodulo === "conformidad-uso" && formData.recibo_pago_nro) {
        if (reciboValidacion.valido === false || reciboValidacion.valido === null) {
          setReciboErrorDialogOpen(true);
          return;
        }
      }
      
      await axios.post(`${API}/api/desarrollo-urbano/${endpoint}`, formData, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      setReciboValidacion({ validando: false, valido: null, mensaje: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleUpdate = async () => {
    try {
      const endpoint = getEndpoint(submodulo);
      
      // Validar campos obligatorios para boletín
      if (submodulo === "boletin-calculo" && !validateBoletinForm()) {
        return;
      }
      
      // Validar recibo de pago para conformidad de uso
      if (submodulo === "conformidad-uso" && formData.recibo_pago_nro) {
        if (reciboValidacion.valido === false || reciboValidacion.valido === null) {
          setReciboErrorDialogOpen(true);
          return;
        }
      }
      
      await axios.put(`${API}/api/desarrollo-urbano/${endpoint}/${editingItem.id}`, formData, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      setReciboValidacion({ validando: false, valido: null, mensaje: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    // Para boletín-cálculo, cambiar estatus a "Anulado" en lugar de eliminar
    if (submodulo === "boletin-calculo") {
      if (!window.confirm("¿Está seguro de anular este boletín? El registro permanecerá en la bandeja con estatus 'Anulado'.")) return;
      try {
        await axios.put(`${API}/api/desarrollo-urbano/boletines-calculo/${id}`, { estatus: "Anulado" }, { headers });
        toast.success("Boletín anulado exitosamente");
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al anular boletín");
      }
    } else if (submodulo === "conformidad-uso") {
      // Para conformidad-uso, cambiar estatus a "Anulada" en lugar de eliminar
      if (!window.confirm("¿Está seguro de anular esta conformidad de uso? El registro permanecerá con estatus 'Anulada'.")) return;
      try {
        await axios.put(`${API}/api/desarrollo-urbano/conformidad-uso/${id}`, { estatus: "Anulada" }, { headers });
        toast.success("Conformidad de uso anulada exitosamente");
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al anular conformidad de uso");
      }
    } else {
      // Para otros submódulos, eliminar normalmente
      if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
      try {
        const endpoint = getEndpoint(submodulo);
        await axios.delete(`${API}/api/desarrollo-urbano/${endpoint}/${id}`, { headers });
        toast.success("Registro eliminado exitosamente");
        fetchData();
      } catch (error) {
        toast.error("Error al eliminar registro");
      }
    }
  };

  // Función para imprimir Conformidad de Uso
  const handlePrintConformidad = async (item) => {
    // Obtener configuración para logos y nombres
    let configData = {};
    try {
      const response = await axios.get(`${API}/api/definiciones/configuracion/actual`, { headers });
      configData = response.data;
    } catch (error) {
      console.error("Error obteniendo configuración:", error);
    }
    
    const printWindow = window.open('', '_blank');
    
    // Generar datos para el QR
    const qrData = JSON.stringify({
      nro_uc: item.nro_uc,
      rif: item.rif,
      fecha_emision: item.fecha_emision,
      estatus: item.estatus
    });
    
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrData)}`;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Conformidad de Uso - ${item.nro_uc}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; }
          
          .header-container { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            margin-bottom: 20px;
            border-bottom: 3px solid #1e40af;
            padding-bottom: 15px;
          }
          .logo-container { width: 90px; height: 90px; }
          .logo-container img { max-width: 100%; max-height: 100%; object-fit: contain; }
          .header-text { flex: 1; text-align: center; padding: 0 20px; }
          .header-text h1 { font-size: 14px; font-weight: bold; margin-bottom: 5px; color: #1e40af; }
          .header-text h2 { font-size: 12px; font-weight: bold; margin-bottom: 3px; }
          .header-text h3 { font-size: 11px; margin-bottom: 3px; color: #555; }
          .header-text h4 { 
            font-size: 16px; 
            font-weight: bold; 
            margin-top: 10px; 
            color: #1e40af;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          
          .doc-number {
            background: #1e40af;
            color: white;
            padding: 10px 20px;
            font-size: 14px;
            font-weight: bold;
            text-align: center;
            margin: 15px 0;
            border-radius: 5px;
          }
          
          .info-section { margin-bottom: 15px; }
          .info-section h3 { 
            background: #e5e7eb; 
            padding: 8px 12px; 
            margin-bottom: 10px; 
            font-size: 12px; 
            border-left: 4px solid #1e40af;
            color: #1e40af;
          }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 0 12px; }
          .info-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; padding: 0 12px; }
          .info-item { display: flex; font-size: 11px; margin-bottom: 5px; }
          .info-label { font-weight: bold; min-width: 150px; color: #374151; }
          .info-value { color: #000; }
          
          .estatus-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 15px;
            font-size: 11px;
            font-weight: bold;
          }
          .estatus-solvente { background: #dcfce7; color: #166534; }
          .estatus-moroso { background: #fef3c7; color: #92400e; }
          .estatus-anulada { background: #fee2e2; color: #991b1b; }
          
          .qr-section {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
          }
          .qr-container { text-align: center; }
          .qr-container img { width: 90px; height: 90px; }
          .qr-container p { font-size: 8px; color: #666; margin-top: 5px; }
          
          .signature-area { display: flex; justify-content: space-around; flex: 1; }
          .signature-box { text-align: center; width: 200px; }
          .signature-line { border-top: 1px solid #333; margin-top: 60px; padding-top: 5px; font-size: 10px; }
          
          .nota-legal {
            margin-top: 25px;
            padding: 12px;
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            font-size: 9px;
            text-align: justify;
            line-height: 1.5;
          }
          .nota-legal strong { font-size: 10px; color: #1e40af; }
          
          .footer { 
            margin-top: 20px; 
            text-align: center; 
            font-size: 9px; 
            color: #6b7280;
            border-top: 1px solid #e5e7eb;
            padding-top: 10px;
          }
          
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header-container">
          <div class="logo-container">
            ${configData.logo_alcaldia ? `<img src="${configData.logo_alcaldia}" alt="Logo Alcaldía" />` : '<div style="width:80px;height:80px;border:1px solid #ccc;display:flex;align-items:center;justify-content:center;font-size:8px;color:#999;">Logo Alcaldía</div>'}
          </div>
          <div class="header-text">
            <h1>REPÚBLICA BOLIVARIANA DE VENEZUELA</h1>
            <h2>${configData.nombre_estado || "ESTADO MIRANDA"}</h2>
            <h3>${configData.nombre_alcaldia || "ALCALDÍA DEL MUNICIPIO ZAMORA"}</h3>
            <h3>Dirección de Desarrollo Urbano</h3>
            <h4>CONFORMIDAD DE USO</h4>
          </div>
          <div class="logo-container">
            ${configData.logo_municipio ? `<img src="${configData.logo_municipio}" alt="Logo Municipio" />` : '<div style="width:80px;height:80px;border:1px solid #ccc;display:flex;align-items:center;justify-content:center;font-size:8px;color:#999;">Logo Municipio</div>'}
          </div>
        </div>
        
        <div class="doc-number">
          N° ${item.nro_uc}
        </div>
        
        <div class="info-section">
          <h3>DATOS DEL CONTRIBUYENTE</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Clase Contribuyente:</span>
              <span class="info-value">${item.clase_contribuyente || '-'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">RIF:</span>
              <span class="info-value">${item.rif || '-'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Nombre/Razón Social:</span>
              <span class="info-value">${item.nombre_razon_social || '-'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Fecha de Emisión:</span>
              <span class="info-value">${item.fecha_emision || '-'}</span>
            </div>
          </div>
        </div>
        
        <div class="info-section">
          <h3>DATOS DEL COMERCIO</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">RIF Comercio:</span>
              <span class="info-value">${item.rif_comercio || '-'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Nombre Comercio:</span>
              <span class="info-value">${item.nombre_comercio || '-'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Ficha Catastral:</span>
              <span class="info-value">${item.ficha_catastral || '0'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Año Conformidad:</span>
              <span class="info-value">${item.anio_conformidad || '-'}</span>
            </div>
          </div>
        </div>
        
        <div class="info-section">
          <h3>DATOS DEL INMUEBLE</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Uso Propuesto:</span>
              <span class="info-value">${item.uso_propuesto || '-'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Uso Aprobado:</span>
              <span class="info-value">${item.uso_aprobado || '-'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Condiciones Físicas:</span>
              <span class="info-value">${item.condiciones_fisicas || '-'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">M² Construcción:</span>
              <span class="info-value">${item.m2_construccion ? item.m2_construccion.toLocaleString('es-VE') + ' m²' : '-'}</span>
            </div>
            <div class="info-item" style="grid-column: span 2;">
              <span class="info-label">Dirección/Ubicación:</span>
              <span class="info-value">${item.direccion_ubicacion || '-'}</span>
            </div>
            <div class="info-item" style="grid-column: span 2;">
              <span class="info-label">Zonificación:</span>
              <span class="info-value">${item.zonificacion || '-'}</span>
            </div>
          </div>
        </div>
        
        <div class="info-section">
          <h3>ESTADO DEL TRÁMITE</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Recibo Pago N°:</span>
              <span class="info-value">${item.recibo_pago_nro || '-'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Exoneración:</span>
              <span class="info-value">${item.exoneracion || 'NO'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Estatus:</span>
              <span class="info-value">
                <span class="estatus-badge ${item.estatus === 'Solvente' ? 'estatus-solvente' : item.estatus === 'Anulada' ? 'estatus-anulada' : 'estatus-moroso'}">
                  ${item.estatus || 'Moroso'}
                </span>
              </span>
            </div>
          </div>
        </div>
        
        <div class="qr-section">
          <div class="qr-container">
            <img src="${qrImageUrl}" alt="Código QR" />
            <p>Código de verificación</p>
          </div>
          <div class="signature-area">
            <div class="signature-box">
              <div class="signature-line">Director de Desarrollo Urbano</div>
            </div>
            <div class="signature-box">
              <div class="signature-line">Sello de la Dirección</div>
            </div>
          </div>
        </div>
        
        <div class="nota-legal">
          <strong>NOTA IMPORTANTE:</strong> Este documento certifica que el uso señalado está conforme con la zonificación establecida 
          en el Plan de Desarrollo Urbano Local vigente. La presente conformidad de uso tiene validez únicamente para el año fiscal 
          indicado y debe ser renovada anualmente. El incumplimiento de las normas urbanísticas vigentes será sancionado conforme a 
          la Ley Orgánica de Ordenación Urbanística y las Ordenanzas Municipales aplicables. Este documento no autoriza el ejercicio 
          de actividades económicas sin la correspondiente Licencia de Actividad Económica.
        </div>
        
        <div class="footer">
          Documento generado el ${new Date().toLocaleString('es-VE')} | ${configData.nombre_alcaldia || 'Alcaldía Municipal'} - Sistema de Recaudación Tributaria SIRET
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Función para imprimir Boletín de Cálculo de Desarrollo Urbano
  const handlePrintBoletinDU = async (item) => {
    if (!item) return;
    
    // Obtener configuración para logos y nombres
    let configData = {};
    try {
      const response = await axios.get(`${API}/api/definiciones/configuracion/actual`, { headers });
      configData = response.data || {};
    } catch (error) {
      console.error("Error obteniendo configuración:", error);
    }
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Por favor, permita ventanas emergentes para imprimir");
      return;
    }
    
    // Generar filas de la tabla de impuestos
    const lineasHTML = (item.lineas_impuestos || []).map(linea => `
      <tr>
        <td>${linea.impuesto_tasa || linea.tipo_impuesto_nombre || '-'}</td>
        <td class="text-right">Bs. ${(linea.ingreso_bruto || linea.valor_catastral || 0).toLocaleString('es-VE', {minimumFractionDigits: 2})}</td>
        <td class="text-right">Bs. ${(linea.importe || 0).toLocaleString('es-VE', {minimumFractionDigits: 2})}</td>
        <td class="text-right">Bs. ${(linea.sub_total || 0).toLocaleString('es-VE', {minimumFractionDigits: 2})}</td>
      </tr>
    `).join('');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Boletín de Cálculo - Desarrollo Urbano - ${item.nro_boletin}</title>
        <style>
          @page { 
            size: letter portrait; 
            margin: 10mm;
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            font-size: 10px; 
            line-height: 1.3;
            background: white;
            color: #1e293b;
            padding: 15px;
          }
          
          /* Encabezado estilo Boletín de Cálculo */
          .header-container { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            margin-bottom: 15px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .logo-container { width: 80px; height: 80px; }
          .logo-container img { max-width: 100%; max-height: 100%; object-fit: contain; }
          .header-text { flex: 1; text-align: center; padding: 0 15px; }
          .header-text h1 { font-size: 12px; font-weight: bold; margin-bottom: 3px; }
          .header-text h2 { font-size: 11px; font-weight: bold; margin-bottom: 2px; }
          .header-text h3 { font-size: 10px; margin-bottom: 2px; }
          .header-text h4 { font-size: 13px; font-weight: bold; margin-top: 5px; text-decoration: underline; }
          
          .boletin-number { 
            background: #333; 
            color: white; 
            padding: 8px 15px; 
            font-size: 12px; 
            font-weight: bold; 
            text-align: center; 
            margin-bottom: 15px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .estatus-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 15px;
            font-weight: bold;
            font-size: 10px;
            text-transform: uppercase;
          }
          .estatus-registrado { background: #dbeafe; color: #1d4ed8; }
          .estatus-enviado { background: #fef3c7; color: #d97706; }
          .estatus-pagado { background: #dcfce7; color: #16a34a; }
          .estatus-anulado { background: #fee2e2; color: #dc2626; }
          
          .info-row { display: flex; gap: 15px; margin-bottom: 15px; }
          .info-section { 
            flex: 1; 
            background: #f8fafc; 
            border: 1px solid #e2e8f0; 
            border-radius: 8px; 
            padding: 12px;
          }
          .section-title { 
            font-size: 11px; 
            font-weight: bold; 
            color: #0e7490; 
            border-bottom: 2px solid #0891b2; 
            padding-bottom: 5px; 
            margin-bottom: 10px;
            text-transform: uppercase;
          }
          .field { margin-bottom: 8px; }
          .field-label { font-size: 8px; color: #64748b; text-transform: uppercase; margin-bottom: 2px; }
          .field-value { font-size: 11px; color: #1e293b; font-weight: 500; }
          .field-highlight { 
            background: #ecfeff; 
            padding: 6px 10px; 
            border-radius: 5px; 
            border-left: 3px solid #0891b2;
          }
          
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          
          .impuestos-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 15px;
            font-size: 9px;
          }
          .impuestos-table th { 
            background: #0891b2; 
            color: white; 
            padding: 8px 10px; 
            text-align: left;
            font-weight: bold;
            text-transform: uppercase;
            font-size: 8px;
          }
          .impuestos-table td { 
            padding: 8px 10px; 
            border-bottom: 1px solid #e2e8f0;
          }
          .impuestos-table tr:nth-child(even) { background: #f8fafc; }
          .text-right { text-align: right; }
          
          .totales-section { 
            display: flex; 
            justify-content: flex-end; 
            margin-bottom: 15px;
          }
          .totales-box { 
            background: linear-gradient(135deg, #0891b2, #0e7490);
            color: white; 
            padding: 15px 25px; 
            border-radius: 8px;
            min-width: 250px;
          }
          .total-row { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 5px;
            font-size: 10px;
          }
          .total-row.final { 
            font-size: 14px; 
            font-weight: bold; 
            border-top: 1px solid rgba(255,255,255,0.3); 
            padding-top: 8px; 
            margin-top: 8px;
          }
          
          .signatures { 
            display: flex; 
            justify-content: space-around; 
            margin-top: 30px;
            padding-top: 20px;
          }
          .signature-box { text-align: center; width: 200px; }
          .signature-line { 
            border-top: 1px solid #333; 
            margin-top: 50px; 
            padding-top: 5px; 
            font-size: 9px;
          }
          .director-du { position: relative; }
          .director-du .firma-img { 
            position: absolute; 
            bottom: 30px; 
            left: 50%; 
            transform: translateX(-50%); 
            max-width: 120px; 
            max-height: 50px; 
            object-fit: contain;
          }
          .director-du .signature-line { margin-top: 55px; }
          .director-nombre { font-weight: normal; font-size: 8px; color: #333; }
          
          .nota-legal {
            margin-top: 15px;
            padding: 10px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-left: 3px solid #3b82f6;
            font-size: 8px;
            text-align: justify;
            line-height: 1.4;
            color: #475569;
          }
          .nota-legal strong {
            color: #1e40af;
          }
          
          .footer { 
            text-align: center; 
            font-size: 8px; 
            color: #94a3b8; 
            margin-top: 10px;
            padding-top: 8px;
            border-top: 1px solid #e2e8f0;
          }
          
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header-container">
          <div class="logo-container">
            ${configData.logo_encabezado_izquierdo ? `<img src="${configData.logo_encabezado_izquierdo}" alt="Escudo" />` : '<div style="width:80px;height:80px;border:1px dashed #ccc;display:flex;align-items:center;justify-content:center;font-size:8px;color:#999;">Escudo</div>'}
          </div>
          <div class="header-text">
            <h1>REPÚBLICA BOLIVARIANA DE VENEZUELA</h1>
            <h2>${configData.nombre_alcaldia || 'ALCALDÍA DEL MUNICIPIO ARISMENDI'}</h2>
            <h3>DIRECCIÓN DE DESARROLLO URBANO</h3>
            <h4>BOLETÍN DE CÁLCULO</h4>
          </div>
          <div class="logo-container">
            ${configData.logo_encabezado_derecho ? `<img src="${configData.logo_encabezado_derecho}" alt="Logo Institucional" />` : '<div style="width:80px;height:80px;border:1px dashed #ccc;display:flex;align-items:center;justify-content:center;font-size:8px;color:#999;">Logo</div>'}
          </div>
        </div>
        
        <div class="boletin-number">
          <span>BOLETÍN Nro. ${item.nro_boletin || 'N/A'} | CÓDIGO CATASTRAL: ${item.codigo_catastral || 'N/A'}</span>
          <span class="estatus-badge ${
            item.estatus === 'Registrado' ? 'estatus-registrado' : 
            item.estatus === 'Enviado a Caja' ? 'estatus-enviado' :
            item.estatus === 'Pagado' ? 'estatus-pagado' : 'estatus-anulado'
          }">${item.estatus}</span>
        </div>
        
        <div class="info-row">
          <div class="info-section">
            <div class="section-title">Datos del Contribuyente</div>
            <div class="field">
              <div class="field-label">Clase de Contribuyente</div>
              <div class="field-value field-highlight">${item.clase_contribuyente || "-"}</div>
            </div>
            <div class="info-grid">
              <div class="field">
                <div class="field-label">RIF</div>
                <div class="field-value">${item.rif || "-"}</div>
              </div>
              <div class="field">
                <div class="field-label">Fecha de Emisión</div>
                <div class="field-value">${item.fecha || "-"}</div>
              </div>
            </div>
            <div class="field">
              <div class="field-label">Nombre / Razón Social</div>
              <div class="field-value">${item.nombre_razon_social || "-"}</div>
            </div>
          </div>
          
          <div class="info-section">
            <div class="section-title">Datos del Inmueble</div>
            <div class="field">
              <div class="field-label">Código Catastral</div>
              <div class="field-value field-highlight">${item.codigo_catastral || "-"}</div>
            </div>
            <div class="info-grid">
              <div class="field">
                <div class="field-label">Período</div>
                <div class="field-value">${item.mes_anio_boletin || "-"}</div>
              </div>
              <div class="field">
                <div class="field-label">Estatus de Pago</div>
                <div class="field-value">${item.estatus_pago || "-"}</div>
              </div>
            </div>
          </div>
        </div>
        
        <table class="impuestos-table">
          <thead>
            <tr>
              <th>Tipo de Impuesto / Tasa</th>
              <th class="text-right">Base Imponible</th>
              <th class="text-right">IMPORTE</th>
              <th class="text-right">Sub-Total</th>
            </tr>
          </thead>
          <tbody>
            ${lineasHTML || '<tr><td colspan="4" style="text-align:center;color:#94a3b8;">Sin líneas de impuestos</td></tr>'}
          </tbody>
        </table>
        
        <div class="totales-section">
          <div class="totales-box">
            <div class="total-row">
              <span>Sub-Total:</span>
              <span>Bs. ${((item.lineas_impuestos || []).reduce((acc, l) => acc + (l.sub_total || 0), 0)).toLocaleString('es-VE', {minimumFractionDigits: 2})}</span>
            </div>
            <div class="total-row">
              <span>Reintegro:</span>
              <span>Bs. ${(item.reintegro || 0).toLocaleString('es-VE', {minimumFractionDigits: 2})}</span>
            </div>
            <div class="total-row">
              <span>Retención:</span>
              <span>Bs. ${(item.retencion || 0).toLocaleString('es-VE', {minimumFractionDigits: 2})}</span>
            </div>
            <div class="total-row">
              <span>Multa:</span>
              <span>Bs. ${(item.multa || 0).toLocaleString('es-VE', {minimumFractionDigits: 2})}</span>
            </div>
            <div class="total-row">
              <span>Descuento:</span>
              <span>Bs. ${(item.descuento || 0).toLocaleString('es-VE', {minimumFractionDigits: 2})}</span>
            </div>
            <div class="total-row final">
              <span>TOTAL A PAGAR:</span>
              <span>Bs. ${(item.total || 0).toLocaleString('es-VE', {minimumFractionDigits: 2})}</span>
            </div>
          </div>
        </div>
        
        <div class="signatures">
          <div class="signature-box">
            <div class="signature-line">Firma del Contribuyente</div>
          </div>
          <div class="signature-box director-du">
            ${configData.firma_director_desarrollo_urbano && configData.firma_director_desarrollo_urbano.startsWith('data:image') 
              ? `<img src="${configData.firma_director_desarrollo_urbano}" alt="Firma Director" class="firma-img" />` 
              : ''}
            <div class="signature-line">
              <strong>DIRECTOR(A) DE DESARROLLO URBANO</strong>
              ${configData.nombre_director_desarrollo_urbano ? `<br/><span class="director-nombre">${configData.nombre_director_desarrollo_urbano}</span>` : ''}
            </div>
          </div>
        </div>
        
        <div class="nota-legal">
          <strong>NOTA IMPORTANTE:</strong> Todos los montos expresados en este documento son calculados de acuerdo a lo establecido en la Ley Orgánica de Coordinación y Armonización de las Potestades Tributarias de los Estados y Municipios (LOCAPTEM), la Resolución N° 011-2023 publicada en gaceta oficial N° 6.783 de fecha 29 de diciembre de 2023 y las ordenanzas municipales correspondientes en materia tributaria.
        </div>
        
        <div class="footer">
          <p>Documento generado el ${new Date().toLocaleString('es-VE')} | Sistema SIRET - Sistema de Recaudación Tributaria Municipal</p>
          <p>Este documento es válido únicamente para el período y código catastral indicados.</p>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const openCreateDialog = async () => {
    setEditingItem(null);
    const today = new Date();
    const fechaHoy = today.toISOString().split('T')[0];
    // Formato YYYY-MM para el campo type="month"
    const mesAnioActual = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

    // Auto-montaje de tipos de impuesto anclados a este módulo
    let anclados = [];
    try {
      const res = await axios.get(`${API}/api/definiciones/tipos-impuesto/anclados/desarrollo-urbano`, { headers });
      anclados = (res.data || []).map((ti) => ({
        tipo_impuesto_id: ti.id,
        tipo_impuesto_nombre: ti.nombre,
        concepto: ti.nombre,
        descripcion: ti.descripcion || ti.nombre,
        tasa: ti.tasa || 0,
        cantidad: 1,
        sub_total: 0,
        codigo_presupuestario_id: ti.codigo_presupuestario_id || null,
        _anclado: true,
      }));
    } catch (err) { console.warn("[anclados/desarrollo-urbano]", err); toast.warning("No se pudieron cargar los impuestos anclados"); }

    setFormData({ 
      fecha: fechaHoy,
      mes_anio_boletin: mesAnioActual,
      estatus: "Registrado",
      estatus_pago: "Sin pagar",
      reintegro: 0,
      retencion: 0,
      multa: 0,
      descuento: 0,
      lineas_impuestos: anclados,
    });
    setSinCodigoCatastral(false);
    setCodigosCatastralesPorRif([]);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setInitialEditLoad(true);
    setFormData(item);
    // Si el código catastral es "0", marcar el checkbox
    setSinCodigoCatastral(item.codigo_catastral === "0");
    if (item.clase_contribuyente) {
      fetchContribuyentes(item.clase_contribuyente);
    }
    // Cargar códigos catastrales si tiene RIF
    if (item.rif) {
      fetchCodigosCatastralesPorRif(item.rif);
    }
    setDialogOpen(true);
  };

  // Agregar línea de impuesto
  const agregarLineaImpuesto = () => {
    const nuevaLinea = {
      tipo_impuesto_id: null,
      impuesto_tasa: "",
      ingreso_bruto: 0,
      importe: 0,
      sub_total: 0
    };
    setFormData({
      ...formData,
      lineas_impuestos: [...(formData.lineas_impuestos || []), nuevaLinea]
    });
  };

  // Eliminar línea de impuesto
  const eliminarLineaImpuesto = (index) => {
    const lineas = [...(formData.lineas_impuestos || [])];
    const tipoIdEliminado = lineas[index]?.tipo_impuesto_id;
    lineas.splice(index, 1);
    
    // Verificar si quedan más líneas del mismo tipo
    const quedanLineasDelTipo = lineas.some(l => l.tipo_impuesto_id === tipoIdEliminado);
    
    // Solo eliminar de tipos seleccionados si no quedan más líneas de ese tipo
    let tiposSeleccionados = formData.tipos_impuesto_seleccionados || [];
    if (!quedanLineasDelTipo) {
      tiposSeleccionados = tiposSeleccionados.filter(id => id !== tipoIdEliminado);
    }
    
    setFormData({ 
      ...formData, 
      lineas_impuestos: lineas,
      tipos_impuesto_seleccionados: tiposSeleccionados
    });
  };

  // Funciones para "Enviar a Caja"
  const handleEnviarCajaClick = (boletin) => {
    if (!esMesActual(boletin.fecha)) {
      setBoletinParaEnviar(boletin);
      setFechaCajaDialogOpen(true);
    } else {
      setEnviarConFechaActualizada(false);
      setBoletinParaEnviar(boletin);
      setEnviarCajaDialogOpen(true);
    }
  };

  const handleConfirmarEnviarCaja = async () => {
    if (!boletinParaEnviar) return;
    
    try {
      const url = `${API}/api/desarrollo-urbano/boletines-calculo/${boletinParaEnviar.id}/enviar-caja${enviarConFechaActualizada ? '?actualizar_fecha=true' : ''}`;
      const response = await axios.post(url, {}, { headers });
      
      toast.success(
        `Boletín ${boletinParaEnviar.nro_boletin} enviado a caja exitosamente. ` +
        `Planilla generada: ${response.data.nro_planilla}`
      );
      
      setEnviarCajaDialogOpen(false);
      setBoletinParaEnviar(null);
      
      // Actualizar la lista de boletines
      fetchData();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Error al enviar a caja");
      }
    }
  };

  // Filter data based on search or multi-filters
  const filteredData = data.filter(item => {
    // Filtros específicos para Boletín - Cálculo
    if (submodulo === "boletin-calculo") {
      const matchNroBoletin = !boletinDUFilters.nro_boletin || 
        (item.nro_boletin || "").toLowerCase().includes(boletinDUFilters.nro_boletin.toLowerCase());
      const matchRif = !boletinDUFilters.rif || 
        (item.rif || "").toLowerCase().includes(boletinDUFilters.rif.toLowerCase());
      const matchNombreRazon = !boletinDUFilters.nombre_razon_social || 
        (item.nombre_razon_social || "").toLowerCase().includes(boletinDUFilters.nombre_razon_social.toLowerCase());
      const matchFecha = !boletinDUFilters.fecha || 
        (item.fecha || "").toLowerCase().includes(boletinDUFilters.fecha.toLowerCase());
      const matchCodigoCatastral = !boletinDUFilters.codigo_catastral || 
        (item.codigo_catastral || "").toLowerCase().includes(boletinDUFilters.codigo_catastral.toLowerCase());
      const matchEstatus = !boletinDUFilters.estatus || 
        (item.estatus || "").toLowerCase().includes(boletinDUFilters.estatus.toLowerCase());
      const matchEstatusPago = !boletinDUFilters.estatus_pago || 
        (item.estatus_pago || "").toLowerCase().includes(boletinDUFilters.estatus_pago.toLowerCase());
      
      return matchNroBoletin && matchRif && matchNombreRazon && matchFecha && matchCodigoCatastral && matchEstatus && matchEstatusPago;
    }

    // Filtros específicos para Conformidad de Uso
    if (submodulo === "conformidad-uso") {
      const matchNroConformidad = !conformidadFilters.nro_conformidad || 
        (item.nro_uc || "").toLowerCase().includes(conformidadFilters.nro_conformidad.toLowerCase());
      const matchRifContribuyente = !conformidadFilters.rif_contribuyente || 
        ((item.rif || "") + " " + (item.nombre_razon_social || "")).toLowerCase().includes(conformidadFilters.rif_contribuyente.toLowerCase());
      const matchRif = !conformidadFilters.rif || 
        (item.rif_comercio || item.rif || "").toLowerCase().includes(conformidadFilters.rif.toLowerCase());
      const matchNombreRazon = !conformidadFilters.nombre_razon_social || 
        (item.nombre_comercio || "").toLowerCase().includes(conformidadFilters.nombre_razon_social.toLowerCase());
      const matchFechaEmision = !conformidadFilters.fecha_emision || 
        (item.fecha_emision || "").toLowerCase().includes(conformidadFilters.fecha_emision.toLowerCase());
      const matchDireccion = !conformidadFilters.direccion || 
        (item.ficha_catastral || "").toLowerCase().includes(conformidadFilters.direccion.toLowerCase());
      const matchEstatus = !conformidadFilters.estatus || 
        (item.estatus || "").toLowerCase().includes(conformidadFilters.estatus.toLowerCase());
      
      return matchNroConformidad && matchRifContribuyente && matchRif && matchNombreRazon && matchFechaEmision && matchDireccion && matchEstatus;
    }

    // Filtro general para otros submódulos
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      item.nro_boletin?.toLowerCase().includes(searchLower) ||
      item.rif?.toLowerCase().includes(searchLower) ||
      item.nombre_razon_social?.toLowerCase().includes(searchLower) ||
      item.codigo_catastral?.toLowerCase().includes(searchLower)
    );
  });

  // Export functions for Boletín - Cálculo
  const exportBoletinToExcel = () => {
    const exportData = data.map(item => ({
      'Nro. Boletín': item.nro_boletin,
      'RIF': item.rif,
      'Nombre / Razón Social': item.nombre_razon_social,
      'Fecha': item.fecha,
      'Código Catastral': item.codigo_catastral,
      'm² o Unidad(es)': item.total_m2,
      'Total (Bs.)': item.total_bs,
      'Estatus': item.estatus
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Boletines');
    XLSX.writeFile(wb, `boletines_calculo_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportBoletinToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(16);
    doc.text('Boletín - Cálculo', 14, 15);
    doc.setFontSize(10);
    doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-VE')}`, 14, 22);
    
    const tableData = data.map(item => [
      item.nro_boletin,
      item.rif || '-',
      item.nombre_razon_social || '-',
      item.fecha || '-',
      item.codigo_catastral || '-',
      item.total_m2?.toFixed(2) || '0.00',
      item.total_bs?.toFixed(2) || '0.00',
      item.estatus
    ]);
    
    autoTable(doc, {
      head: [['Nro. Boletín', 'RIF', 'Nombre/Razón Social', 'Fecha', 'Cód. Catastral', 'm²/Unid.', 'Total Bs.', 'Estatus']],
      body: tableData,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [99, 102, 241] }
    });
    
    doc.save(`boletines_calculo_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportBoletinToCSV = () => {
    const headers = ['Nro. Boletín', 'RIF', 'Nombre / Razón Social', 'Fecha', 'Código Catastral', 'm² o Unidad(es)', 'Total (Bs.)', 'Estatus'];
    let csv = headers.join(',') + '\n';
    
    data.forEach(item => {
      csv += [
        item.nro_boletin,
        item.rif || '',
        `"${(item.nombre_razon_social || '').replace(/"/g, '""')}"`,
        item.fecha || '',
        item.codigo_catastral || '',
        item.total_m2?.toFixed(2) || '0.00',
        item.total_bs?.toFixed(2) || '0.00',
        item.estatus
      ].join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `boletines_calculo_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportBoletinToText = () => {
    let text = 'BOLETÍN - CÁLCULO\n';
    text += `Fecha de generación: ${new Date().toLocaleDateString('es-VE')}\n`;
    text += '='.repeat(100) + '\n\n';
    
    data.forEach((item, index) => {
      text += `Registro #${index + 1}\n`;
      text += '-'.repeat(50) + '\n';
      text += `Nro. Boletín: ${item.nro_boletin}\n`;
      text += `RIF: ${item.rif || '-'}\n`;
      text += `Nombre / Razón Social: ${item.nombre_razon_social || '-'}\n`;
      text += `Fecha: ${item.fecha || '-'}\n`;
      text += `Código Catastral: ${item.codigo_catastral || '-'}\n`;
      text += `m² o Unidad(es): ${item.total_m2?.toFixed(2) || '0.00'}\n`;
      text += `Total (Bs.): ${item.total_bs?.toFixed(2) || '0.00'}\n`;
      text += `Estatus: ${item.estatus}\n\n`;
    });
    
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `boletines_calculo_${new Date().toISOString().split('T')[0]}.txt`);
  };

  const exportBoletinToHTML = () => {
    const headers = ['Nro. Boletín', 'RIF', 'Nombre / Razón Social', 'Fecha', 'Código Catastral', 'm² o Unidad(es)', 'Total (Bs.)', 'Estatus'];
    let html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Boletín - Cálculo</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h1 { color: #4f46e5; }
    table { border-collapse: collapse; width: 100%; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #6366f1; color: white; }
    tr:nth-child(even) { background-color: #f2f2f2; }
    .text-right { text-align: right; }
  </style>
</head>
<body>
  <h1>Boletín - Cálculo</h1>
  <p>Fecha de generación: ${new Date().toLocaleDateString('es-VE')}</p>
  <table>
    <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>`;
    
    data.forEach(item => {
      html += `<tr>
        <td>${item.nro_boletin}</td>
        <td>${item.rif || '-'}</td>
        <td>${item.nombre_razon_social || '-'}</td>
        <td>${item.fecha || '-'}</td>
        <td>${item.codigo_catastral || '-'}</td>
        <td class="text-right">${item.total_m2?.toFixed(2) || '0.00'}</td>
        <td class="text-right">${item.total_bs?.toFixed(2) || '0.00'}</td>
        <td>${item.estatus}</td>
      </tr>`;
    });
    
    html += `</tbody></table></body></html>`;
    
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    saveAs(blob, `boletines_calculo_${new Date().toISOString().split('T')[0]}.html`);
  };

  const exportBoletinToJSON = () => {
    const exportData = data.map(item => ({
      nro_boletin: item.nro_boletin,
      rif: item.rif,
      nombre_razon_social: item.nombre_razon_social,
      fecha: item.fecha,
      codigo_catastral: item.codigo_catastral,
      total_m2: item.total_m2,
      total_bs: item.total_bs,
      estatus: item.estatus
    }));
    
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    saveAs(blob, `boletines_calculo_${new Date().toISOString().split('T')[0]}.json`);
  };

  // Export functions for Conformidad de Uso
  const exportConformidadToExcel = () => {
    const exportData = data.map(item => ({
      'Nro. UC': item.nro_uc,
      'RIF Comercio': item.rif_comercio || item.rif || '',
      'Nombre Comercio': item.nombre_comercio || '',
      'Fecha Emisión': item.fecha_emision || '',
      'Ficha Catastral': item.ficha_catastral || '',
      'Uso Propuesto': item.uso_propuesto || '',
      'Uso Aprobado': item.uso_aprobado || '',
      'M2 Construcción': item.m2_construccion || '',
      'Dirección': item.direccion_ubicacion || '',
      'Zonificación': item.zonificacion || '',
      'Recibo Pago': item.recibo_pago_nro || '',
      'Estatus': item.estatus || ''
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Conformidad de Uso');
    XLSX.writeFile(wb, `conformidad_uso_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportConformidadToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(16);
    doc.text('Conformidad de Uso', 14, 15);
    doc.setFontSize(10);
    doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-VE')}`, 14, 22);
    
    const tableData = data.map(item => [
      item.nro_uc,
      item.rif_comercio || item.rif || '-',
      item.nombre_comercio || '-',
      item.fecha_emision || '-',
      item.ficha_catastral || '-',
      item.uso_aprobado || '-',
      item.m2_construccion || '-',
      item.estatus
    ]);
    
    autoTable(doc, {
      head: [['Nro. UC', 'RIF', 'Nombre Comercio', 'Fecha', 'Ficha Catastral', 'Uso Aprobado', 'M2', 'Estatus']],
      body: tableData,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [139, 92, 246] }
    });
    
    doc.save(`conformidad_uso_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportConformidadToCSV = () => {
    const headers = ['Nro. UC', 'RIF Comercio', 'Nombre Comercio', 'Fecha Emisión', 'Ficha Catastral', 'Uso Propuesto', 'Uso Aprobado', 'M2 Construcción', 'Dirección', 'Zonificación', 'Recibo Pago', 'Estatus'];
    let csv = headers.join(',') + '\n';
    
    data.forEach(item => {
      csv += [
        item.nro_uc || '',
        item.rif_comercio || item.rif || '',
        `"${(item.nombre_comercio || '').replace(/"/g, '""')}"`,
        item.fecha_emision || '',
        item.ficha_catastral || '',
        `"${(item.uso_propuesto || '').replace(/"/g, '""')}"`,
        `"${(item.uso_aprobado || '').replace(/"/g, '""')}"`,
        item.m2_construccion || '',
        `"${(item.direccion_ubicacion || '').replace(/"/g, '""')}"`,
        item.zonificacion || '',
        item.recibo_pago_nro || '',
        item.estatus || ''
      ].join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `conformidad_uso_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportConformidadToText = () => {
    let text = 'CONFORMIDAD DE USO\n';
    text += `Fecha de generación: ${new Date().toLocaleDateString('es-VE')}\n`;
    text += '='.repeat(100) + '\n\n';
    
    data.forEach((item, index) => {
      text += `Registro #${index + 1}\n`;
      text += '-'.repeat(50) + '\n';
      text += `Nro. UC: ${item.nro_uc || '-'}\n`;
      text += `RIF Comercio: ${item.rif_comercio || item.rif || '-'}\n`;
      text += `Nombre Comercio: ${item.nombre_comercio || '-'}\n`;
      text += `Fecha Emisión: ${item.fecha_emision || '-'}\n`;
      text += `Ficha Catastral: ${item.ficha_catastral || '-'}\n`;
      text += `Uso Propuesto: ${item.uso_propuesto || '-'}\n`;
      text += `Uso Aprobado: ${item.uso_aprobado || '-'}\n`;
      text += `M2 Construcción: ${item.m2_construccion || '-'}\n`;
      text += `Dirección: ${item.direccion_ubicacion || '-'}\n`;
      text += `Zonificación: ${item.zonificacion || '-'}\n`;
      text += `Recibo Pago: ${item.recibo_pago_nro || '-'}\n`;
      text += `Estatus: ${item.estatus || '-'}\n\n`;
    });
    
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `conformidad_uso_${new Date().toISOString().split('T')[0]}.txt`);
  };

  const exportConformidadToHTML = () => {
    const headers = ['Nro. UC', 'RIF Comercio', 'Nombre Comercio', 'Fecha Emisión', 'Ficha Catastral', 'Uso Aprobado', 'M2', 'Estatus'];
    let html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Conformidad de Uso</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h1 { color: #8b5cf6; }
    table { border-collapse: collapse; width: 100%; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #8b5cf6; color: white; }
    tr:nth-child(even) { background-color: #f2f2f2; }
  </style>
</head>
<body>
  <h1>Conformidad de Uso</h1>
  <p>Fecha de generación: ${new Date().toLocaleDateString('es-VE')}</p>
  <table>
    <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>`;
    
    data.forEach(item => {
      html += `<tr>
        <td>${item.nro_uc || '-'}</td>
        <td>${item.rif_comercio || item.rif || '-'}</td>
        <td>${item.nombre_comercio || '-'}</td>
        <td>${item.fecha_emision || '-'}</td>
        <td>${item.ficha_catastral || '-'}</td>
        <td>${item.uso_aprobado || '-'}</td>
        <td>${item.m2_construccion || '-'}</td>
        <td>${item.estatus || '-'}</td>
      </tr>`;
    });
    
    html += `</tbody></table></body></html>`;
    
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    saveAs(blob, `conformidad_uso_${new Date().toISOString().split('T')[0]}.html`);
  };

  const exportConformidadToJSON = () => {
    const exportData = data.map(item => ({
      nro_uc: item.nro_uc,
      rif_comercio: item.rif_comercio || item.rif,
      nombre_comercio: item.nombre_comercio,
      fecha_emision: item.fecha_emision,
      ficha_catastral: item.ficha_catastral,
      uso_propuesto: item.uso_propuesto,
      uso_aprobado: item.uso_aprobado,
      condiciones_fisicas: item.condiciones_fisicas,
      m2_construccion: item.m2_construccion,
      direccion_ubicacion: item.direccion_ubicacion,
      zonificacion: item.zonificacion,
      recibo_pago_nro: item.recibo_pago_nro,
      exoneracion: item.exoneracion,
      estatus: item.estatus
    }));
    
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    saveAs(blob, `conformidad_uso_${new Date().toISOString().split('T')[0]}.json`);
  };

  const submodulos = [
    { 
      id: "boletin-calculo", 
      title: "Boletín - Cálculo", 
      description: "Cálculo de impuestos de desarrollo urbano", 
      icon: FileSpreadsheet, 
      color: "indigo" 
    },
    { 
      id: "conformidad-uso", 
      title: "Conformidad de Uso", 
      description: "Registro de conformidad de uso", 
      icon: Building2, 
      color: "violet" 
    }
  ];

  // Clases de contribuyente options
  const clasesContribuyente = [
    "Contribuyente con LAE",
    "Contribuyente sin LAE"
  ];

  // Render submenu cards if no submodule selected
  if (!submodulo) {
    return (
      <div className="space-y-6" data-testid="desarrollo-urbano-page">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Desarrollo Urbano
            </h1>
            <p className="text-slate-500">Gestión de impuestos de desarrollo urbano</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {submodulos.map((sub) => {
            const Icon = sub.icon;
            return (
              <Card 
                key={sub.id}
                className="card-hover cursor-pointer group" 
                onClick={() => setSearchParams({ sub: sub.id })}
                data-testid={`submodulo-${sub.id}`}
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 bg-${sub.color}-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-6 h-6 text-${sub.color}-600`} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{sub.title}</h3>
                  <p className="text-sm text-slate-500">{sub.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Get current submodule info
  const currentSubmodulo = submodulos.find(s => s.id === submodulo);

  return (
    <div className="space-y-6" data-testid="desarrollo-urbano-submodulo">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setSearchParams({})}
          className="hover:bg-slate-100"
          data-testid="btn-volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
          <Building2 className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {currentSubmodulo?.title || "Desarrollo Urbano"}
          </h1>
          <p className="text-slate-500">{currentSubmodulo?.description}</p>
        </div>
      </div>

      {/* Submodule content - Boletín Cálculo */}
      {submodulo === "boletin-calculo" && (
        <div className="space-y-4">
          {/* Search and Add */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            {/* Indicador de filtros activos */}
            <div className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
                <span className="font-bold text-blue-800">{filteredData.length}</span>
                <span className="text-blue-600">de</span>
                <span className="font-bold text-blue-800">{data.length}</span>
                <span className="text-blue-600">registros</span>
              </div>
              {Object.values(boletinDUFilters).some(v => v !== "") && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setBoletinDUFilters({
                    nro_boletin: "",
                    rif: "",
                    nombre_razon_social: "",
                    fecha: "",
                    codigo_catastral: "",
                    estatus: "",
                    estatus_pago: ""
                  })}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Limpiar filtros
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="btn-exportar-boletin">
                    <Download className="w-4 h-4 mr-2" />
                    Exportar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportBoletinToExcel()} data-testid="btn-export-excel">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportBoletinToPDF()} data-testid="btn-export-pdf">
                    <File className="w-4 h-4 mr-2" />
                    PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportBoletinToCSV()} data-testid="btn-export-csv">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportBoletinToText()} data-testid="btn-export-text">
                    <File className="w-4 h-4 mr-2" />
                    Texto
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportBoletinToHTML()} data-testid="btn-export-html">
                    <FileType className="w-4 h-4 mr-2" />
                    HTML
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportBoletinToJSON()} data-testid="btn-export-json">
                    <FileJson className="w-4 h-4 mr-2" />
                    JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button 
                onClick={openCreateDialog}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="btn-nuevo"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Boletín
              </Button>
            </div>
          </div>

          {/* Data table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Nro. Boletín</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">RIF</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Nombre / Razón Social</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Fecha</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Código Catastral</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">m² o Unidad(es)</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Total (Bs.)</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Estatus</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Estatus Pago</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Acciones</th>
                    </tr>
                    {/* Fila de filtros */}
                    <tr className="border-b bg-white">
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={boletinDUFilters.nro_boletin} onChange={(e) => setBoletinDUFilters({...boletinDUFilters, nro_boletin: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={boletinDUFilters.rif} onChange={(e) => setBoletinDUFilters({...boletinDUFilters, rif: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={boletinDUFilters.nombre_razon_social} onChange={(e) => setBoletinDUFilters({...boletinDUFilters, nombre_razon_social: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={boletinDUFilters.fecha} onChange={(e) => setBoletinDUFilters({...boletinDUFilters, fecha: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={boletinDUFilters.codigo_catastral} onChange={(e) => setBoletinDUFilters({...boletinDUFilters, codigo_catastral: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2"></th>
                      <th className="px-2 py-2"></th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={boletinDUFilters.estatus} onChange={(e) => setBoletinDUFilters({...boletinDUFilters, estatus: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={boletinDUFilters.estatus_pago} onChange={(e) => setBoletinDUFilters({...boletinDUFilters, estatus_pago: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-slate-500">
                          No hay boletines registrados
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((item) => (
                        <tr key={item.id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.nro_boletin}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.rif}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.nombre_razon_social}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.fecha}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.codigo_catastral}</td>
                          <td className="px-4 py-3 text-sm text-slate-900 text-right">
                            {item.metros_cuadrados?.toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">
                            {item.total?.toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.estatus === "Enviado a Caja" 
                                ? "bg-blue-100 text-blue-800" 
                                : item.estatus === "Anulado"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-slate-100 text-slate-800"
                            }`}>
                              {item.estatus || "Registrado"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.estatus_pago === "Pagado" 
                                ? "bg-green-100 text-green-800" 
                                : "bg-amber-100 text-amber-800"
                            }`}>
                              {item.estatus_pago || "Sin pagar"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`row-actions-${item.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(item)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handlePrintBoletinDU(item)}>
                                  <Printer className="w-4 h-4 mr-2" />
                                  Imprimir
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEnviarCajaClick(item)} data-testid={`btn-enviar-caja-${item.id}`}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Enviar a Caja
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(item.id)}
                                  className="text-amber-600"
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Anular
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Form Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingItem ? "Editar Boletín - Cálculo" : "Nuevo Boletín - Cálculo"}
                </DialogTitle>
              </DialogHeader>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
                {/* Clase de Contribuyente */}
                <div className="space-y-2">
                  <Label>Clase de Contribuyente <span className="text-red-500">*</span></Label>
                  <Select 
                    value={formData.clase_contribuyente || ""} 
                    onValueChange={(value) => setFormData({...formData, clase_contribuyente: value})}
                  >
                    <SelectTrigger data-testid="form-clase">
                      <SelectValue placeholder="Seleccione clase" />
                    </SelectTrigger>
                    <SelectContent>
                      {clasesContribuyente.map((clase) => (
                        <SelectItem key={clase} value={clase}>{clase}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* RIF / Contribuyente */}
                <div className="space-y-2">
                  <Label>RIF / Contribuyente <span className="text-red-500">*</span></Label>
                  <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={rifPopoverOpen}
                        className="w-full justify-between h-10 font-normal"
                        disabled={!formData.clase_contribuyente}
                        data-testid="form-rif"
                      >
                        {formData.rif ? (
                          <span className="truncate">
                            {contribuyentes.find(c => c.id === formData.contribuyente_id)?.display_name || 
                             (formData.nombre_razon_social ? `${formData.rif} - ${formData.nombre_razon_social}` : formData.rif)}
                          </span>
                        ) : (
                          <span className="text-slate-500">
                            {formData.clase_contribuyente ? "Seleccione contribuyente..." : "Primero seleccione clase"}
                          </span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Buscar por RIF o nombre..." />
                        <CommandList>
                          <CommandEmpty>No se encontraron contribuyentes</CommandEmpty>
                          <CommandGroup>
                            {contribuyentes.map((contribuyente) => (
                              <CommandItem
                                key={contribuyente.id}
                                value={contribuyente.display_name}
                                onSelect={() => {
                                  const rifCompleto = `${contribuyente.tipo_rif}-${contribuyente.rif}`;
                                  setFormData({
                                    ...formData,
                                    rif: rifCompleto,
                                    contribuyente_id: contribuyente.id,
                                    nombre_razon_social: contribuyente.nombre || contribuyente.nombre_razon_social || contribuyente.razon_social,
                                    codigo_catastral: "" // Limpiar código catastral al cambiar RIF
                                  });
                                  setRifPopoverOpen(false);
                                  // Cargar códigos catastrales para este RIF
                                  fetchCodigosCatastralesPorRif(rifCompleto);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    formData.contribuyente_id === contribuyente.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                {contribuyente.display_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Fecha */}
                <div className="space-y-2">
                  <Label>Fecha <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={formData.fecha || ""}
                    onChange={(e) => setFormData({...formData, fecha: e.target.value})}
                    data-testid="form-fecha"
                  />
                </div>

                {/* Mes/Año Boletín */}
                <div className="space-y-2">
                  <Label>Mes/Año Boletín <span className="text-red-500">*</span></Label>
                  <Input
                    type="month"
                    value={formData.mes_anio_boletin || ""}
                    onChange={(e) => setFormData({...formData, mes_anio_boletin: e.target.value})}
                    data-testid="form-mes-anio"
                  />
                </div>

                {/* Código Catastral - Dropdown con checkbox Sin Código */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Código Catastral</Label>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sin-codigo-catastral"
                        checked={sinCodigoCatastral}
                        onCheckedChange={(checked) => {
                          setSinCodigoCatastral(checked);
                          if (checked) {
                            setFormData({...formData, codigo_catastral: "0"});
                          } else {
                            setFormData({...formData, codigo_catastral: ""});
                          }
                        }}
                        data-testid="checkbox-sin-codigo-catastral"
                      />
                      <label
                        htmlFor="sin-codigo-catastral"
                        className="text-sm font-medium leading-none cursor-pointer text-slate-600"
                      >
                        Sin Código Catastral
                      </label>
                    </div>
                  </div>
                  
                  {sinCodigoCatastral ? (
                    <div 
                      className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-700 cursor-not-allowed"
                      data-testid="form-codigo-catastral-disabled"
                    >
                      0
                    </div>
                  ) : (
                    <Popover open={codigoCatastralPopoverOpen} onOpenChange={setCodigoCatastralPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          role="combobox" 
                          className="w-full justify-between h-10 font-normal" 
                          disabled={!formData.rif}
                          data-testid="form-codigo-catastral"
                        >
                          {formData.codigo_catastral ? (
                            <span className="truncate">{formData.codigo_catastral}</span>
                          ) : (
                            <span className="text-slate-500">
                              {formData.rif ? "Seleccione código catastral..." : "Primero seleccione RIF"}
                            </span>
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput 
                            placeholder="Buscar código catastral..." 
                            value={codigoCatastralBusqueda}
                            onValueChange={setCodigoCatastralBusqueda}
                          />
                          <CommandList>
                            <CommandEmpty>
                              {codigosCatastralesPorRif.length === 0 
                                ? "No hay fichas catastrales para este contribuyente" 
                                : "No se encontraron resultados"}
                            </CommandEmpty>
                            <CommandGroup>
                              {codigosCatastralesPorRif
                                .filter(item => 
                                  item.codigo.toLowerCase().includes(codigoCatastralBusqueda.toLowerCase()) ||
                                  item.direccion.toLowerCase().includes(codigoCatastralBusqueda.toLowerCase())
                                )
                                .map((item) => (
                                  <CommandItem
                                    key={item.id}
                                    value={item.codigo}
                                    onSelect={() => {
                                      setFormData({
                                        ...formData,
                                        codigo_catastral: item.codigo
                                      });
                                      setCodigoCatastralPopoverOpen(false);
                                      setCodigoCatastralBusqueda("");
                                    }}
                                  >
                                    <Check className={`mr-2 h-4 w-4 ${formData.codigo_catastral === item.codigo ? "opacity-100" : "opacity-0"}`} />
                                    <div className="flex flex-col">
                                      <span className="font-medium">{item.codigo}</span>
                                      {item.direccion && (
                                        <span className="text-xs text-slate-500 truncate max-w-[300px]">{item.direccion}</span>
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                  {!sinCodigoCatastral && formData.rif && codigosCatastralesPorRif.length === 0 && (
                    <p className="text-xs text-amber-600">No hay fichas catastrales registradas para este RIF</p>
                  )}
                </div>

                {/* m² o Unidad(es) */}
                <div className="space-y-2">
                  <Label>m² o Unidad(es)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.metros_cuadrados || ""}
                    onChange={(e) => setFormData({...formData, metros_cuadrados: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                    className="text-right"
                    data-testid="form-metros"
                  />
                </div>

                {/* Oficina - Fijo */}
                <div className="space-y-2">
                  <Label>Oficina</Label>
                  <div 
                    className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-700 cursor-not-allowed"
                    data-testid="form-oficina-boletin-desarrollo"
                  >
                    Desarrollo Urbano
                  </div>
                </div>

                {/* Tipo de Impuesto - Selección Múltiple */}
                <div className="space-y-2 md:col-span-3">
                  <div className="flex items-center gap-2">
                    <Label>Tipo de Impuesto <span className="text-red-500">*</span></Label>
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                      Oficina: Desarrollo Urbano
                    </span>
                    <span className="text-xs text-slate-500">(Selección múltiple)</span>
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between h-auto min-h-10 font-normal"
                        data-testid="form-tipos-impuesto"
                      >
                        <div className="flex flex-wrap gap-1 py-1">
                          {(formData.tipos_impuesto_seleccionados || []).length > 0 ? (
                            (formData.tipos_impuesto_seleccionados || []).map((tipoId) => {
                              const tipo = tiposImpuesto.find(t => t.id === tipoId);
                              return tipo ? (
                                <span key={tipoId} className="bg-indigo-100 text-indigo-800 text-xs px-2 py-0.5 rounded-full">
                                  {tipo.nombre}
                                </span>
                              ) : null;
                            })
                          ) : (
                            <span className="text-slate-500">Seleccione tipos de impuesto...</span>
                          )}
                        </div>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <div className="p-2 border-b">
                        <p className="text-sm font-medium text-slate-700">Seleccione los tipos de impuesto</p>
                        <p className="text-xs text-slate-500">Puede seleccionar múltiples opciones</p>
                      </div>
                      <div className="p-2 border-b">
                        <Input
                          placeholder="Buscar tipo de impuesto..."
                          value={tipoImpuestoBusqueda}
                          onChange={(e) => setTipoImpuestoBusqueda(e.target.value)}
                          className="h-8"
                          data-testid="search-tipo-impuesto"
                        />
                      </div>
                      <div className="max-h-[300px] overflow-y-auto p-2">
                        {tiposImpuesto
                          .filter(tipo => tipo.nombre.toLowerCase().includes(tipoImpuestoBusqueda.toLowerCase()))
                          .map((tipo) => {
                          const isSelected = (formData.tipos_impuesto_seleccionados || []).includes(tipo.id);
                          
                          // Función para agregar una línea de este tipo (permite repetir)
                          const agregarLinea = (e) => {
                            e.stopPropagation(); // Evitar que se dispare el onClick del div padre
                            const currentLineas = formData.lineas_impuestos || [];
                            
                            // Verificar si es tipo Tasa o Urbano
                            const esTipoTasa = tipo.tipo_calculo_id === TIPO_CALCULO_TASA_ID;
                            const esTipoUrbano = tipo.tipo_calculo_id === TIPO_CALCULO_URBANO_ID;
                            const unidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
                            const metrosCuadrados = formData.metros_cuadrados || 0;
                            
                            let ingresoBruto = 0;
                            let importe = tipo.alicuota || 0;
                            
                            if (esTipoTasa) {
                              ingresoBruto = unidadCuenta;
                              importe = tipo.tasa || 0;
                            } else if (esTipoUrbano) {
                              const tcmmv = parseFloat(tipo.tcmmv) || 0;
                              ingresoBruto = tcmmv * unidadCuenta * metrosCuadrados;
                              importe = tipo.tasa || 0;
                            }
                            
                            const subTotal = esTipoUrbano 
                              ? ingresoBruto * (importe / 100)
                              : ingresoBruto * importe;
                            
                            // Crear nueva línea con ID único para permitir repetidos
                            const nuevaLinea = {
                              linea_id: `${tipo.id}_${Date.now()}`, // ID único para cada línea
                              tipo_impuesto_id: tipo.id,
                              impuesto_tasa: tipo.nombre,
                              ingreso_bruto: ingresoBruto,
                              importe: importe,
                              sub_total: subTotal,
                              es_tipo_tasa: esTipoTasa,
                              es_tipo_urbano: esTipoUrbano,
                              tcmmv: parseFloat(tipo.tcmmv) || 0
                            };
                            
                            // Agregar a tipos seleccionados si no está
                            const currentTipos = formData.tipos_impuesto_seleccionados || [];
                            const updatedTipos = currentTipos.includes(tipo.id) 
                              ? currentTipos 
                              : [...currentTipos, tipo.id];
                            
                            setFormData({
                              ...formData,
                              tipos_impuesto_seleccionados: updatedTipos,
                              lineas_impuestos: [...currentLineas, nuevaLinea]
                            });
                          };
                          
                          return (
                            <div
                              key={tipo.id}
                              className={`flex items-center gap-2 p-2 rounded hover:bg-slate-100 ${isSelected ? 'bg-indigo-50' : ''}`}
                            >
                              <div 
                                className="flex items-center gap-2 flex-1 cursor-pointer"
                                onClick={() => {
                                  const current = formData.tipos_impuesto_seleccionados || [];
                                  const currentLineas = formData.lineas_impuestos || [];
                                  let updatedTipos;
                                  let updatedLineas;
                                  
                                  if (isSelected) {
                                    // Deseleccionar: quitar de la lista y eliminar TODAS las líneas de este tipo
                                    updatedTipos = current.filter(id => id !== tipo.id);
                                    updatedLineas = currentLineas.filter(l => l.tipo_impuesto_id !== tipo.id);
                                  } else {
                                    // Seleccionar: agregar a la lista y crear línea automáticamente
                                    updatedTipos = [...current, tipo.id];
                                    
                                    // Verificar si es tipo Tasa o Urbano
                                    const esTipoTasa = tipo.tipo_calculo_id === TIPO_CALCULO_TASA_ID;
                                    const esTipoUrbano = tipo.tipo_calculo_id === TIPO_CALCULO_URBANO_ID;
                                    const unidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
                                    const metrosCuadrados = formData.metros_cuadrados || 0;
                                    
                                    let ingresoBruto = 0;
                                    let importe = tipo.alicuota || 0;
                                    
                                    if (esTipoTasa) {
                                      ingresoBruto = unidadCuenta;
                                      importe = tipo.tasa || 0;
                                    } else if (esTipoUrbano) {
                                      const tcmmv = parseFloat(tipo.tcmmv) || 0;
                                      ingresoBruto = tcmmv * unidadCuenta * metrosCuadrados;
                                      importe = tipo.tasa || 0;
                                    }
                                    
                                    const subTotal = esTipoUrbano 
                                      ? ingresoBruto * (importe / 100)
                                      : ingresoBruto * importe;
                                    
                                    const nuevaLinea = {
                                      linea_id: `${tipo.id}_${Date.now()}`,
                                      tipo_impuesto_id: tipo.id,
                                      impuesto_tasa: tipo.nombre,
                                      ingreso_bruto: ingresoBruto,
                                      importe: importe,
                                      sub_total: subTotal,
                                      es_tipo_tasa: esTipoTasa,
                                      es_tipo_urbano: esTipoUrbano,
                                      tcmmv: parseFloat(tipo.tcmmv) || 0
                                    };
                                    
                                    updatedLineas = [...currentLineas, nuevaLinea];
                                  }
                                  
                                  setFormData({
                                    ...formData, 
                                    tipos_impuesto_seleccionados: updatedTipos,
                                    lineas_impuestos: updatedLineas
                                  });
                                }}
                              >
                                <div className={`w-4 h-4 border rounded flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                                  {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm truncate block">{tipo.nombre}</span>
                                  {tipo.alicuota && <span className="text-xs text-slate-500">(Alícuota: {tipo.alicuota})</span>}
                                </div>
                              </div>
                              {/* Botón para repetir/agregar otra línea del mismo tipo */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100"
                                onClick={agregarLinea}
                                title="Agregar otra línea de este tipo"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Líneas de Impuestos/Tasas - Selección Múltiple */}
                <div className="md:col-span-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label className="text-base font-semibold">Líneas de Impuestos/Tasas <span className="text-red-500">*</span></Label>
                      <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                        Oficina: Desarrollo Urbano
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">Puede agregar múltiples líneas y repetir tipos de impuesto si es necesario</p>
                  
                  <div className="border rounded-lg overflow-hidden overflow-x-auto">
                    <table className="w-full table-fixed min-w-[700px]">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 w-[40%]">IMPUESTOS/TASAS</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-slate-600 w-[18%]">INGRESO BRUTO</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-slate-600 w-[15%]">IMPORTE</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-slate-600 w-[18%]">SUB TOTAL</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-slate-600 w-[9%]">ACCIÓN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(formData.lineas_impuestos || []).length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-3 py-4 text-center text-slate-400 text-sm">
                              No hay líneas de impuestos. Seleccione tipos de impuesto arriba para agregar líneas automáticamente.
                            </td>
                          </tr>
                        ) : (
                          (formData.lineas_impuestos || []).map((linea, index) => (
                            <tr key={linea.linea_id || index} className="border-t" data-testid="linea-impuesto-row" data-tipo-nombre={linea.impuesto_tasa || linea.tipo_impuesto_nombre || linea.concepto || ""} data-anclado={linea._anclado ? "true" : "false"}>
                              <td className="px-3 py-2">
                                <Select
                                  value={linea.tipo_impuesto_id || ""}
                                  onValueChange={(value) => {
                                    const tipoSeleccionado = tiposImpuesto.find(t => t.id === value);
                                    const lineas = [...(formData.lineas_impuestos || [])];
                                    const unidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
                                    const metrosCuadrados = formData.metros_cuadrados || 0; // Usar campo global
                                    
                                    // Verificar si el tipo de cálculo es "Tasa" o "Urbano"
                                    const esTipoTasa = tipoSeleccionado?.tipo_calculo_id === TIPO_CALCULO_TASA_ID;
                                    const esTipoUrbano = tipoSeleccionado?.tipo_calculo_id === TIPO_CALCULO_URBANO_ID;
                                    
                                    let ingresoBruto = lineas[index].ingreso_bruto || 0;
                                    let importe = tipoSeleccionado?.alicuota || 0;
                                    const tcmmv = parseFloat(tipoSeleccionado?.tcmmv) || 0;
                                    
                                    if (esTipoTasa) {
                                      // Tasa: Ingreso Bruto = Unidad de Cuenta, Importe = Tasa del tipo
                                      ingresoBruto = unidadCuenta;
                                      importe = tipoSeleccionado?.tasa || 0;
                                    } else if (esTipoUrbano) {
                                      // Urbano: Ingreso Bruto = TCMMV × Unidad de Cuenta × m² (campo global)
                                      // Importe = Tasa del tipo de impuesto
                                      ingresoBruto = tcmmv * unidadCuenta * metrosCuadrados;
                                      importe = tipoSeleccionado?.tasa || 0;
                                    }
                                    
                                    // Para urbano: subTotal = ingresoBruto * (importe / 100)
                                    // Para tasa: subTotal = ingresoBruto * importe
                                    const subTotal = esTipoUrbano 
                                      ? ingresoBruto * (importe / 100)
                                      : ingresoBruto * importe;
                                    
                                    lineas[index] = { 
                                      ...lineas[index], 
                                      tipo_impuesto_id: value,
                                      impuesto_tasa: tipoSeleccionado?.nombre || "",
                                      ingreso_bruto: ingresoBruto,
                                      importe: importe,
                                      sub_total: subTotal,
                                      es_tipo_tasa: esTipoTasa,
                                      es_tipo_urbano: esTipoUrbano,
                                      tcmmv: tcmmv
                                    };
                                    
                                    setFormData({ ...formData, lineas_impuestos: lineas });
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-sm truncate">
                                    <SelectValue placeholder="Seleccione tipo..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {tiposImpuesto.map((tipo) => (
                                      <SelectItem key={tipo.id} value={tipo.id}>
                                        {tipo.nombre}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={linea.ingreso_bruto?.toFixed(2) || ""}
                                  onChange={(e) => {
                                    // No permitir edición si es tipo Tasa o Urbano
                                    if (linea.es_tipo_tasa || linea.es_tipo_urbano) return;
                                    const lineas = [...(formData.lineas_impuestos || [])];
                                    const ingresoBruto = parseFloat(e.target.value) || 0;
                                    const importe = lineas[index].importe || 0;
                                    const subTotal = ingresoBruto * importe;
                                    lineas[index] = { 
                                      ...lineas[index], 
                                      ingreso_bruto: ingresoBruto,
                                      sub_total: subTotal
                                    };
                                    setFormData({ ...formData, lineas_impuestos: lineas });
                                  }}
                                  placeholder="0.00"
                                  className={`h-8 text-sm text-right w-full ${(linea.es_tipo_tasa || linea.es_tipo_urbano) ? 'bg-slate-100' : ''}`}
                                  readOnly={linea.es_tipo_tasa || linea.es_tipo_urbano}
                                  title={linea.es_tipo_tasa ? 'Automático: Unidad de Cuenta' : linea.es_tipo_urbano ? 'Automático: TCMMV × U.C. × m²' : ''}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={linea.importe || ""}
                                  onChange={(e) => {
                                    // No permitir edición si es tipo Tasa o Urbano
                                    if (linea.es_tipo_tasa || linea.es_tipo_urbano) return;
                                    const lineas = [...(formData.lineas_impuestos || [])];
                                    const importe = parseFloat(e.target.value) || 0;
                                    const ingresoBruto = lineas[index].ingreso_bruto || 0;
                                    const subTotal = ingresoBruto * importe;
                                    lineas[index] = { 
                                      ...lineas[index], 
                                      importe: importe,
                                      sub_total: subTotal
                                    };
                                    setFormData({ ...formData, lineas_impuestos: lineas });
                                  }}
                                  placeholder="0.00"
                                  className={`h-8 text-sm text-right w-full ${(linea.es_tipo_tasa || linea.es_tipo_urbano) ? 'bg-slate-100' : ''}`}
                                  readOnly={linea.es_tipo_tasa || linea.es_tipo_urbano}
                                  title={linea.es_tipo_tasa ? 'Automático: Tasa del impuesto' : linea.es_tipo_urbano ? 'Automático: Tasa del impuesto (%)' : ''}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={linea.sub_total?.toFixed(2) || ""}
                                  readOnly
                                  className="h-8 text-sm text-right bg-slate-50 w-full"
                                />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => eliminarLineaImpuesto(index)}
                                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <MinusCircle className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Campos de totales */}
                <div className="space-y-2">
                  <Label>Descuento</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.descuento || 0}
                    onChange={(e) => setFormData({...formData, descuento: parseFloat(e.target.value) || 0})}
                    className="text-right"
                    data-testid="form-descuento"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Reintegro</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.reintegro || 0}
                    onChange={(e) => setFormData({...formData, reintegro: parseFloat(e.target.value) || 0})}
                    className="text-right"
                    data-testid="form-reintegro"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Retención</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.retencion || 0}
                    onChange={(e) => setFormData({...formData, retencion: parseFloat(e.target.value) || 0})}
                    className="text-right"
                    data-testid="form-retencion"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Multa</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.multa || 0}
                    onChange={(e) => setFormData({...formData, multa: parseFloat(e.target.value) || 0})}
                    className="text-right"
                    data-testid="form-multa"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Divisas ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.divisas?.toFixed(2) || "0.00"}
                    readOnly
                    className="text-right bg-slate-50"
                    data-testid="form-divisas-usd"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Divisas (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.divisas_euro?.toFixed(2) || "0.00"}
                    readOnly
                    className="text-right bg-slate-50"
                    data-testid="form-divisas-eur"
                  />
                </div>

                <div className="space-y-2 md:col-span-3">
                  <Label className="text-lg font-semibold">Total (Bs.)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.total?.toFixed(2) || "0.00"}
                    readOnly
                    className="text-right text-xl font-bold bg-indigo-50 border-indigo-200"
                    data-testid="form-total"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={editingItem ? handleUpdate : handleCreate}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="btn-guardar"
                >
                  {editingItem ? "Actualizar" : "Guardar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Submodule content - Conformidad de Uso */}
      {submodulo === "conformidad-uso" && (
        <div className="space-y-4">
          {/* Search and Add */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            {/* Indicador de filtros activos */}
            <div className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
                <span className="font-bold text-blue-800">{filteredData.length}</span>
                <span className="text-blue-600">de</span>
                <span className="font-bold text-blue-800">{data.length}</span>
                <span className="text-blue-600">registros</span>
              </div>
              {Object.values(conformidadFilters).some(v => v !== "") && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setConformidadFilters({
                    nro_conformidad: "",
                    rif: "",
                    nombre_razon_social: "",
                    lae: "",
                    direccion: "",
                    fecha_emision: "",
                    fecha_vencimiento: "",
                    estatus: ""
                  })}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Limpiar filtros
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="btn-exportar-conformidad">
                    <Download className="w-4 h-4 mr-2" />
                    Exportar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportConformidadToExcel} data-testid="export-conformidad-excel">
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                    Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportConformidadToPDF} data-testid="export-conformidad-pdf">
                    <File className="w-4 h-4 mr-2 text-red-600" />
                    PDF (.pdf)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportConformidadToCSV} data-testid="export-conformidad-csv">
                    <FileType className="w-4 h-4 mr-2 text-blue-600" />
                    CSV (.csv)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportConformidadToText} data-testid="export-conformidad-txt">
                    <FileType className="w-4 h-4 mr-2 text-slate-600" />
                    Texto (.txt)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportConformidadToHTML} data-testid="export-conformidad-html">
                    <FileType className="w-4 h-4 mr-2 text-orange-600" />
                    HTML (.html)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportConformidadToJSON} data-testid="export-conformidad-json">
                    <FileJson className="w-4 h-4 mr-2 text-amber-600" />
                    JSON (.json)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button 
                onClick={() => {
                  setEditingItem(null);
                  const today = new Date().toISOString().split('T')[0];
                  const currentYear = new Date().getFullYear().toString();
                  setFormData({ 
                    fecha_emision: today,
                    anio_conformidad: currentYear,
                    clase_contribuyente: "Contribuyente con LAE",
                    estatus: "Moroso",
                    exoneracion: "NO"
                  });
                  setReciboValidacion({ validando: false, valido: null, mensaje: "" });
                  setDialogOpen(true);
                }}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="btn-nuevo-conformidad"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Conformidad
              </Button>
            </div>
          </div>

          {/* Data table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Nro. UC</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">RIF / Contribuyente</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">RIF Comercio</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Nombre Comercio</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Ficha Catastral</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Fecha Emisión</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Uso Aprobado</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Estatus</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Acciones</th>
                    </tr>
                    {/* Fila de filtros */}
                    <tr className="border-b bg-white">
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={conformidadFilters.nro_conformidad} onChange={(e) => setConformidadFilters({...conformidadFilters, nro_conformidad: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={conformidadFilters.rif_contribuyente} onChange={(e) => setConformidadFilters({...conformidadFilters, rif_contribuyente: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={conformidadFilters.rif} onChange={(e) => setConformidadFilters({...conformidadFilters, rif: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={conformidadFilters.nombre_razon_social} onChange={(e) => setConformidadFilters({...conformidadFilters, nombre_razon_social: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={conformidadFilters.direccion} onChange={(e) => setConformidadFilters({...conformidadFilters, direccion: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={conformidadFilters.fecha_emision} onChange={(e) => setConformidadFilters({...conformidadFilters, fecha_emision: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2"></th>
                      <th className="px-2 py-2">
                        <Input placeholder="Filtrar..." value={conformidadFilters.estatus} onChange={(e) => setConformidadFilters({...conformidadFilters, estatus: e.target.value})} className="h-8 text-xs" />
                      </th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-slate-500">
                          No hay registros de conformidad de uso
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((item) => (
                        <tr key={item.id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.nro_uc}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.rif} {item.nombre_razon_social ? `- ${item.nombre_razon_social}` : ""}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.rif_comercio || item.rif}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.nombre_comercio}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.ficha_catastral}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.fecha_emision}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{item.uso_aprobado}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.estatus === "Solvente" 
                                ? "bg-green-100 text-green-800" 
                                : item.estatus === "Moroso"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                            }`}>
                              {item.estatus}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`row-actions-conformidad-${item.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(item)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => toast.info("Función de exoneración en desarrollo")}>
                                  <FileCheck className="w-4 h-4 mr-2" />
                                  Exoneración
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  if (item.estatus === "Solvente") {
                                    handlePrintConformidad(item);
                                  } else {
                                    setPrintWarningEstatus(item.estatus || "Moroso");
                                    setPrintWarningDialogOpen(true);
                                  }
                                }}>
                                  <Printer className="w-4 h-4 mr-2" />
                                  Imprimir
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(item.id)}
                                  className="text-red-600"
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Anular
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Form Dialog - Conformidad de Uso */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingItem ? "Editar Conformidad de Uso" : "Nueva Conformidad de Uso"}
                </DialogTitle>
              </DialogHeader>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
                {/* Clase de Contribuyente */}
                <div className="space-y-2">
                  <Label>Clase de Contribuyente</Label>
                  <Select 
                    value={formData.clase_contribuyente || ""} 
                    onValueChange={(value) => {
                      setFormData({...formData, clase_contribuyente: value, rif: "", contribuyente_id: "", nombre_razon_social: ""});
                    }}
                  >
                    <SelectTrigger data-testid="form-clase-conformidad">
                      <SelectValue placeholder="Seleccione clase" />
                    </SelectTrigger>
                    <SelectContent>
                      {clasesContribuyente.map((clase) => (
                        <SelectItem key={clase} value={clase}>{clase}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* RIF / Contribuyente */}
                <div className="space-y-2">
                  <Label>RIF / Contribuyente</Label>
                  <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={rifPopoverOpen}
                        className="w-full justify-between h-10 font-normal"
                        disabled={!formData.clase_contribuyente}
                        data-testid="form-rif-conformidad"
                      >
                        {formData.rif ? (
                          <span className="truncate">
                            {contribuyentes.find(c => c.id === formData.contribuyente_id)?.display_name || 
                             (formData.nombre_razon_social ? `${formData.rif} - ${formData.nombre_razon_social}` : formData.rif)}
                          </span>
                        ) : (
                          <span className="text-slate-500">
                            {formData.clase_contribuyente ? "Seleccione contribuyente..." : "Primero seleccione clase"}
                          </span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Buscar por RIF o nombre..." />
                        <CommandList>
                          <CommandEmpty>No se encontraron contribuyentes</CommandEmpty>
                          <CommandGroup>
                            {contribuyentes.map((contribuyente) => (
                              <CommandItem
                                key={contribuyente.id}
                                value={contribuyente.display_name}
                                onSelect={async () => {
                                  const rifCompleto = `${contribuyente.tipo_rif}-${contribuyente.rif}`;
                                  // Buscar la ficha catastral automáticamente
                                  const codigoCatastral = await fetchFichaCatastralPorRif(rifCompleto);
                                  setFormData({
                                    ...formData,
                                    rif: rifCompleto,
                                    contribuyente_id: contribuyente.id,
                                    nombre_razon_social: contribuyente.nombre || contribuyente.nombre_razon_social || contribuyente.razon_social,
                                    ficha_catastral: codigoCatastral || ""
                                  });
                                  if (codigoCatastral) {
                                    toast.success(`Ficha Catastral encontrada: ${codigoCatastral}`);
                                  }
                                  setRifPopoverOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    formData.contribuyente_id === contribuyente.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                {contribuyente.display_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Fecha de Emisión */}
                <div className="space-y-2">
                  <Label>Fecha de Emisión</Label>
                  <Input
                    type="date"
                    value={formData.fecha_emision || ""}
                    onChange={(e) => setFormData({...formData, fecha_emision: e.target.value})}
                    data-testid="form-fecha-emision"
                  />
                </div>

                {/* Nro de LAE */}
                <div className="space-y-2">
                  <Label>Nro de LAE</Label>
                  {formData.lae_en_tramite ? (
                    <Input value="" disabled placeholder="LAE en trámite" className="bg-slate-100" data-testid="form-nro-lae" />
                  ) : (
                    <Popover open={nroLaePopoverOpen} onOpenChange={setNroLaePopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between h-10 font-normal" data-testid="form-nro-lae">
                          {formData.nro_lae ? (
                            <span className="truncate">{formData.nro_lae}</span>
                          ) : (
                            <span className="text-slate-500">Buscar por Nro LAE...</span>
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Buscar número de LAE..." />
                          <CommandList>
                            <CommandEmpty>No encontrado</CommandEmpty>
                            <CommandGroup>
                              {contribuyentesLAE.map(c => {
                                const tipoRif = c.tipo_rif || 'J';
                                return (
                                  <CommandItem 
                                    key={c.id} 
                                    value={`${c.numero_lae} ${c.nombre || c.denominacion_comercial || ''}`} 
                                    onSelect={() => {
                                      setFormData({
                                        ...formData, 
                                        nro_lae: c.numero_lae,
                                        rif_comercio: `${tipoRif}-${c.rif}`,
                                        nombre_comercio: c.nombre || c.denominacion_comercial,
                                        direccion_ubicacion: c.domicilio_fiscal || ""
                                      });
                                      setNroLaePopoverOpen(false);
                                    }}
                                  >
                                    <Check className={`mr-2 h-4 w-4 ${formData.nro_lae === c.numero_lae ? "opacity-100" : "opacity-0"}`} />
                                    <div className="flex flex-col">
                                      <span className="font-medium">LAE: {c.numero_lae}</span>
                                      <span className="text-xs text-slate-500">{c.nombre || c.denominacion_comercial} | RIF: {tipoRif}-{c.rif}</span>
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>

                {/* LAE en trámite checkbox */}
                <div className="space-y-2 flex items-center gap-3 pt-6">
                  <input 
                    type="checkbox" 
                    id="lae_en_tramite_conf" 
                    checked={formData.lae_en_tramite || false} 
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({...formData, lae_en_tramite: e.target.checked, nro_lae: "", rif_comercio: "", nombre_comercio: "", direccion_ubicacion: ""});
                      } else {
                        setFormData({...formData, lae_en_tramite: e.target.checked});
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    data-testid="form-lae-tramite"
                  />
                  <Label htmlFor="lae_en_tramite_conf" className="cursor-pointer font-medium">LAE en trámite</Label>
                </div>

                {/* Año de la Conformidad */}
                <div className="space-y-2">
                  <Label>Año de la Conformidad</Label>
                  <Input
                    type="number"
                    min="2000"
                    max="2100"
                    value={formData.anio_conformidad || ""}
                    onChange={(e) => setFormData({...formData, anio_conformidad: e.target.value})}
                    placeholder="Ej: 2026"
                    data-testid="form-anio-conformidad"
                  />
                </div>

                {/* RIF del Comercio */}
                {!formData.lae_en_tramite && (
                <div className="space-y-2">
                  <Label>RIF del Comercio</Label>
                  <Input
                    value={formData.rif_comercio || ""}
                    onChange={(e) => setFormData({...formData, rif_comercio: e.target.value})}
                    placeholder="Ej: J-12345678-9"
                    readOnly={!!formData.nro_lae}
                    className={formData.nro_lae ? "bg-slate-50" : ""}
                    data-testid="form-rif-comercio"
                  />
                </div>
                )}

                {/* Nombre (Razón Social) del Comercio */}
                {!formData.lae_en_tramite && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Nombre (Razón Social) del Comercio</Label>
                  {formData.nro_lae ? (
                    <Input
                      value={formData.nombre_comercio || ""}
                      readOnly
                      className="bg-slate-50"
                      data-testid="form-nombre-comercio"
                    />
                  ) : (
                  <Popover open={nombreComercioPopoverOpen} onOpenChange={setNombreComercioPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between h-10 font-normal" data-testid="form-nombre-comercio">
                        {formData.nombre_comercio ? (
                          <span className="truncate">{formData.nombre_comercio}</span>
                        ) : (
                          <span className="text-slate-500">Seleccione comercio...</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Buscar por nombre..." />
                        <CommandList>
                          <CommandEmpty>No encontrado</CommandEmpty>
                          <CommandGroup>
                            {contribuyentesLAE.map(c => {
                              const tipoRif = c.tipo_rif || 'J';
                              return (
                                <CommandItem 
                                  key={c.id} 
                                  value={c.nombre || c.denominacion_comercial || ""} 
                                  onSelect={() => {
                                    setFormData({
                                      ...formData, 
                                      nombre_comercio: c.nombre || c.denominacion_comercial,
                                      rif_comercio: `${tipoRif}-${c.rif}`,
                                      nro_lae: c.numero_lae,
                                      direccion_ubicacion: c.domicilio_fiscal || ""
                                    });
                                    setNombreComercioPopoverOpen(false);
                                  }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${formData.nombre_comercio === (c.nombre || c.denominacion_comercial) ? "opacity-100" : "opacity-0"}`} />
                                  <div className="flex flex-col">
                                    <span>{c.nombre || c.denominacion_comercial}</span>
                                    <span className="text-xs text-slate-500">RIF: {tipoRif}-{c.rif} | LAE: {c.numero_lae}</span>
                                  </div>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  )}
                </div>
                )}

                {/* Ficha Catastral */}
                <div className="space-y-2">
                  <Label>Ficha Catastral</Label>
                  <Input
                    value={formData.ficha_catastral || ""}
                    onChange={(e) => setFormData({...formData, ficha_catastral: e.target.value, sin_ficha_catastral: false})}
                    placeholder="Ej: 01-01-001-0001"
                    disabled={formData.sin_ficha_catastral}
                    className={formData.sin_ficha_catastral ? "bg-slate-100" : ""}
                    data-testid="form-ficha-catastral"
                  />
                </div>

                {/* Sin Ficha Catastral checkbox */}
                <div className="space-y-2 flex items-center gap-3 pt-6">
                  <input 
                    type="checkbox" 
                    id="sin_ficha_catastral_conf" 
                    checked={formData.sin_ficha_catastral || false} 
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({...formData, sin_ficha_catastral: true, ficha_catastral: "0"});
                      } else {
                        setFormData({...formData, sin_ficha_catastral: false, ficha_catastral: ""});
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    data-testid="form-sin-ficha-catastral"
                  />
                  <Label htmlFor="sin_ficha_catastral_conf" className="cursor-pointer font-medium">Sin Ficha Catastral</Label>
                </div>

                {/* Uso Propuesto */}
                <div className="space-y-2">
                  <Label>Uso Propuesto</Label>
                  <Input
                    value={formData.uso_propuesto || ""}
                    onChange={(e) => setFormData({...formData, uso_propuesto: e.target.value})}
                    placeholder="Uso propuesto"
                    data-testid="form-uso-propuesto"
                  />
                </div>

                {/* Uso Aprobado */}
                <div className="space-y-2">
                  <Label>Uso Aprobado</Label>
                  <Input
                    value={formData.uso_aprobado || ""}
                    onChange={(e) => setFormData({...formData, uso_aprobado: e.target.value})}
                    placeholder="Uso aprobado"
                    data-testid="form-uso-aprobado"
                  />
                </div>

                {/* Condiciones Físicas */}
                <div className="space-y-2">
                  <Label>Condiciones Físicas</Label>
                  <Select 
                    value={formData.condiciones_fisicas || ""} 
                    onValueChange={(value) => setFormData({...formData, condiciones_fisicas: value})}
                  >
                    <SelectTrigger data-testid="form-condiciones-fisicas">
                      <SelectValue placeholder="Seleccione condición" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="APTAS">APTAS</SelectItem>
                      <SelectItem value="NO APTAS">NO APTAS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* M2 de Construcción */}
                <div className="space-y-2">
                  <Label>M2 de Construcción</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.m2_construccion || ""}
                    onChange={(e) => setFormData({...formData, m2_construccion: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                    className="text-right"
                    data-testid="form-m2-construccion"
                  />
                </div>

                {/* Dirección Ubicación */}
                {!formData.lae_en_tramite && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Dirección Ubicación</Label>
                  <Input
                    value={formData.direccion_ubicacion || ""}
                    onChange={(e) => setFormData({...formData, direccion_ubicacion: e.target.value})}
                    placeholder="Dirección completa"
                    readOnly={!!formData.nro_lae}
                    className={formData.nro_lae ? "bg-slate-50" : ""}
                    data-testid="form-direccion"
                  />
                </div>
                )}

                {/* Zonificación */}
                <div className="space-y-2 md:col-span-2">
                  <Label>Zonificación</Label>
                  <Popover open={zonificacionPopoverOpen} onOpenChange={setZonificacionPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={zonificacionPopoverOpen}
                        className="w-full justify-between h-10 font-normal"
                        data-testid="form-zonificacion"
                      >
                        {formData.zonificacion ? (
                          <span className="truncate">{formData.zonificacion}</span>
                        ) : (
                          <span className="text-slate-500">Seleccione zonificación...</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[500px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Buscar zonificación..." />
                        <CommandList className="max-h-[300px]">
                          <CommandEmpty>No se encontró zonificación</CommandEmpty>
                          <CommandGroup>
                            {[
                              "AT-1 RESIDENCIAL CON COMERCIAL, INDUSTRIA Y SERVICIOS",
                              "AT-2 RESIDENCIAL CON COMERCIO",
                              "AR-1 RESIDENCIAL CON COMERCIO",
                              "ZONA AR-2 RESIDENCIAL CON COMERCIO E INDUSTRIA",
                              "ZONA AR-3: RESIDENCIAL CON COMERCIO E INDUSTRIA",
                              "ZONA AR-4: RESIDENCIAL CON COMERCIO Y SERVICIOS",
                              "ZONA AR-5: RESIDENCIAL CON COMERCIO Y TURISTICO",
                              "ZONA AR-6 RESIDENCIAL CON TURISTICO",
                              "ZONA AR-E1: AREAS RESIDENCIALES DE ACCION ESPECIAL",
                              "ZONA ND-2: NUEVOS DESARROLLOS RESIDENCIALES",
                              "ZONA ND-3: NUEVOS DESARROLLOS RESIDENCIALES",
                              "ZONA ND-4: NUEVOS DESARROLLOS RESIDENCIALES",
                              "ZONA ND-5: NUEVOS DESARROLLOS RESIDENCIALES",
                              "ZONA ND-6: NUEVOS DESARROLLOS RESIDENCIALES",
                              "ZONA ND-7: NUEVOS DESARROLLOS RESIDENCIALES",
                              "ZONA ND-D: NUEVOS DESARROLLOS DIFERIDOS",
                              "ZONA AGP: EDIFICACIONES DE USO ADMINISTRATIVO",
                              "ZONA EE: EDUCACIONAL",
                              "ZONA MAE: ZONA MEDICO ASISTENCIAL",
                              "ZONA CSRE: ZONA CULTURAL-SOCIAL",
                              "ZONA CE: CEMENTERIO",
                              "ZONA SPE: SERVICIOS PUBLICOS ESPECIALES",
                              "ZONA RDE: PARQUES PLAZAS Y DEPORTIVOS",
                              "ZONA AP: AREAS DE PROTECCION",
                              "ZONA ARU-1: ZONA PROTECTORA",
                              "ZONA VHA: AREAS DE VALOR HISTORICO AMBIENTAL",
                              "ZONA DE ARQUITECTURA CONTROLADA (ZAC)",
                              "ZONA DE TRANSICION (ZT)",
                              "ZONA C-2: COMERCIO VECINAL",
                              "ZONA C-3 COMERCIO GENERAL",
                              "ZONA C-4 COMERCIO REGIONAL"
                            ].map((zona) => (
                              <CommandItem
                                key={zona}
                                value={zona}
                                onSelect={() => {
                                  setFormData({...formData, zonificacion: zona});
                                  setZonificacionPopoverOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    formData.zonificacion === zona ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                {zona}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Recibo Pago Nro. */}
                <div className="space-y-2">
                  <Label>Recibo Pago Nro.</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.recibo_pago_nro || ""}
                      onChange={(e) => {
                        setFormData({...formData, recibo_pago_nro: e.target.value});
                        setReciboValidacion({ validando: false, valido: null, mensaje: "" });
                      }}
                      onBlur={(e) => {
                        if (e.target.value) {
                          validarReciboConformidad(e.target.value);
                        }
                      }}
                      placeholder="Número de recibo"
                      data-testid="form-recibo-pago"
                      className={`flex-1 ${
                        reciboValidacion.valido === true ? 'border-green-500 bg-green-50' : 
                        reciboValidacion.valido === false ? 'border-red-500 bg-red-50' : ''
                      }`}
                    />
                    {reciboValidacion.validando && (
                      <div className="flex items-center text-slate-500">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      </div>
                    )}
                    {reciboValidacion.valido === true && (
                      <div className="flex items-center text-green-600">
                        <Check className="w-5 h-5" />
                      </div>
                    )}
                    {reciboValidacion.valido === false && (
                      <div className="flex items-center text-red-600">
                        <X className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                  {reciboValidacion.mensaje && reciboValidacion.valido === false && (
                    <p className="text-sm text-red-600">{reciboValidacion.mensaje}</p>
                  )}
                </div>

                {/* Exoneración */}
                <div className="space-y-2">
                  <Label>Exoneración</Label>
                  <Select 
                    value={formData.exoneracion || ""} 
                    onValueChange={(value) => setFormData({...formData, exoneracion: value})}
                  >
                    <SelectTrigger data-testid="form-exoneracion">
                      <SelectValue placeholder="Seleccione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SI">SI</SelectItem>
                      <SelectItem value="NO">NO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Estatus */}
                <div className="space-y-2">
                  <Label>Estatus</Label>
                  <div 
                    className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm cursor-not-allowed"
                    data-testid="form-estatus-conformidad"
                  >
                    <span className={`font-medium ${
                      formData.estatus === "Solvente" ? "text-green-600" : 
                      formData.estatus === "Anulada" ? "text-red-600" : 
                      "text-orange-600"
                    }`}>
                      {formData.estatus || "Moroso"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    El estatus cambia automáticamente al validar el Recibo de Pago
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={editingItem ? handleUpdate : handleCreate}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="btn-guardar-conformidad"
                >
                  {editingItem ? "Actualizar" : "Guardar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Diálogo de confirmación para Enviar a Caja */}
      <AlertDialog open={enviarCajaDialogOpen} onOpenChange={setEnviarCajaDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar envío a caja</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>¿Está usted seguro de enviar este boletín a caja para el proceso de liquidación y pago?</p>
                
                {boletinParaEnviar && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3 border">
                    {/* Datos del Contribuyente */}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Datos del Contribuyente</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-slate-500">RIF:</span>
                          <span className="ml-2 font-medium text-slate-900">{boletinParaEnviar.rif || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Nombre:</span>
                          <span className="ml-2 font-medium text-slate-900">{boletinParaEnviar.nombre_razon_social || "N/A"}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Datos del Boletín */}
                    <div className="border-t pt-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Datos del Boletín</p>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-slate-500">Nro:</span>
                          <span className="ml-2 font-medium text-slate-900">{boletinParaEnviar.nro_boletin || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Fecha:</span>
                          <span className="ml-2 font-medium text-slate-900">{boletinParaEnviar.fecha || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Total:</span>
                          <span className="ml-2 font-semibold text-indigo-700">Bs. {(boletinParaEnviar.total || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setBoletinParaEnviar(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmarEnviarCaja}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de error de validación de recibo */}
      <AlertDialog open={reciboErrorDialogOpen} onOpenChange={setReciboErrorDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <X className="w-6 h-6" />
              No se puede guardar el registro
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 mt-4">
                <p className="text-slate-700">
                  El registro no puede ser guardado debido a que el <strong>Recibo de Pago</strong> ingresado no ha sido validado correctamente.
                </p>
                {reciboValidacion.mensaje && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700 font-medium">Motivo del error:</p>
                    <p className="text-red-600 mt-1">{reciboValidacion.mensaje}</p>
                  </div>
                )}
                <p className="text-slate-600 text-sm">
                  Por favor, verifique que el número de recibo de pago sea correcto y que corresponda al contribuyente y clase de contribuyente seleccionados, con un tipo de impuesto de "Conformidad de Uso".
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setReciboErrorDialogOpen(false)}
              className="bg-red-600 hover:bg-red-700"
            >
              Entendido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de advertencia para impresión - Solo Solventes */}
      <AlertDialog open={printWarningDialogOpen} onOpenChange={setPrintWarningDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <Ban className="w-6 h-6" />
              No se puede imprimir
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 mt-4">
                <p className="text-slate-700">
                  La <strong>Conformidad de Uso</strong> no puede ser impresa porque el contribuyente no se encuentra <strong>Solvente</strong>.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-700 font-medium">Estatus actual:</p>
                  <p className="text-amber-800 mt-1 text-lg font-bold">{printWarningEstatus}</p>
                </div>
                <p className="text-slate-600 text-sm">
                  Para poder imprimir este documento, el contribuyente debe tener un <strong>Recibo de Pago</strong> válido con tipo de impuesto "Conformidad de Uso" registrado, lo cual cambiará automáticamente el estatus a <strong>Solvente</strong>.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setPrintWarningDialogOpen(false)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Entendido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DialogoFechaCaja
        open={fechaCajaDialogOpen}
        onOpenChange={setFechaCajaDialogOpen}
        fechaCalculo={boletinParaEnviar?.fecha}
        onConfirm={() => {
          setFechaCajaDialogOpen(false);
          setEnviarConFechaActualizada(true);
          setEnviarCajaDialogOpen(true);
        }}
        onCancel={() => {
          setFechaCajaDialogOpen(false);
          setBoletinParaEnviar(null);
        }}
      />
    </div>
  );
};

export default DesarrolloUrbanoPage;
